// Public customer quote accept page (no login). This is where the ESA link in
// the quote PDF lands: the customer sees their priced options in plain English,
// picks one, and accepts -- which renders their service agreement for signing.
//
// Design constraint (the most important one): assume zero lift knowledge and
// zero code knowledge. Plain English, branded, works on a phone. House rules:
// double hyphens, no personal names, "RFID" never "NFC", the single client phone
// number 0203 576 4760.
//
// Visual treatment: the V1 STRUCTURE (Session Y's faithful recreation of
// docs/specs/V1-CUSTOMER-QUOTE-SOURCE.js -- the ELE logo in a dark header band, the facts
// strip, the per-tier cards, the dark summary box) on a LIGHT premium-document canvas.
// Session AGZ moved this page to the platform's dark canvas and Session AHD moved it back
// on the Director's decision, so the Session Y light-body rule stands: this is what a
// building manager opens when they are sent a quote link, and it should read as a document
// from a company rather than a page of that company's internal software.
// All styling lives in the scoped ./AcceptQuote.css, as CSS variables on .cq whose values
// are the same on screen and on paper. The page never uses the app shell's classes, so it
// stays independent of the app theme toggle. The accept logic and the { tier } payload are
// unchanged, and have been through both moves untouched.
import { NO_SIGNATORY, SignatoryQuestion, signatoryToSend, type SignatoryAnswer } from "../../components/SignatoryQuestion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { numberWord } from "../../lib/techVisitRate";
import type { PublicQuote, PublicTierKey, PublicLift, PublicEquipmentItem, PublicScheduleOfFeesRates, PublicTechVisitRates } from "../../lib/api";
import { WhatsAppLink } from "../../components/WhatsAppLink";
import { LABOUR_INCREMENT_MINUTES, LABOUR_INCREMENT_SENTENCE, firstHourFigure, repairsPublishedText, repairHourRate, REPAIR_EVENING_START_HOUR } from "../../lib/calloutCharge";
import { PrivacyNoticeLink } from "../../components/PrivacyNoticeLink";
import {
  TERMS_CLAUSES,
  MAINTENANCE_SCHEDULE,
  TERMS_CLAUSES_CARE_EQUIPMENT,
  MAINTENANCE_SCHEDULE_CARE_EQUIPMENT,
  TERMS_CLAUSES_COVERED_ITEMS,
  MAINTENANCE_SCHEDULE_COVERED_ITEMS,
} from "./quoteModalContent";
import { frequencyOptionsForType, coerceFrequencyForType, frequencyLabel } from "../../lib/quotePricing";
import type { QuoteFrequency, FrequencyOption } from "../../lib/quotePricing";
import {
  SKYLINE_WATERMARK_OPACITY_LIGHT,
  SKYLINE_WATERMARK_HEIGHT,
} from "../../lib/skyline";
import "./AcceptQuote.css";

const TIER_LABEL: Record<PublicTierKey, string> = { core: "Core", care: "Care", complete: "Complete" };

// Tier card copy (Session AAV, Loop 3: the clean pricing-card treatment). One short value-led
// line, then a tick list of what is included -- so the three cards compare at a glance. The
// values are the locked tier constructions (Care £250 parts / 15% off; Complete 6 call-outs +
// £400 parts / 25% off); the wording keeps the Session AE value-led framing.
// Exported (Session AJW) for the same reuse-not-fork reason as Footer/TermsModal/
// MaintenanceModal/ShareprintSection: the renewal accept page's tier cards now show this real
// content instead of a second, thinner summary that could drift from the locked tier figures.
// Session AKB: a percentage off an undisclosed number is not a comparable saving (Director), so
// the call-out lines now state the actual figure behind each discount -- £163/£138.55/£122.25,
// the locked in-contract first-hour rates in server/src/modules/pricing/calloutRates.ts's own
// documented matrix (CALLOUT_FIRST_HOUR in server/src/modules/billing/billing.ts) -- rather than
// a bare percentage. Core's line is the anchor the other two are compared against.
export const TIER_CARD_COPY: Record<PublicTierKey, { blurb: string; includes: string[] }> = {
  core: {
    blurb: "Straightforward scheduled servicing, done properly.",
    includes: [
      "Scheduled maintenance visits",
      "Full safety compliance on every visit",
      "All labour for routine adjustments",
      "Call-outs and parts at our Schedule of Fees rates, £163 for the first hour (in-hours)",
    ],
  },
  care: {
    blurb: "Servicing plus parts cover and discounted call-outs. Most customers find the parts cover alone justifies the upgrade.",
    includes: [
      "Everything in Core",
      "Up to £250 parts cover per lift per year",
      "15% off call-out labour, £138.55 instead of £163 for the first hour",
      // Session AMP. This line used to read "Minor wear items included, so routine repairs rarely
      // bring a separate invoice", sitting directly beneath the call-out discount above it. Two
      // things were wrong with that. The allowance covers PARTS, not labour, so a repair still
      // carries an hourly charge; and reading the two lines together invited exactly the inference
      // the Director's ruling exists to kill, that a Care customer's repairs are discounted too.
      // It now says what the allowance actually buys, and the repair rate is stated once, flat, in
      // the comparison table below where its being identical across all three tiers is visible.
      "Minor wear parts included, so small fixes often carry no separate parts charge",
    ],
  },
  complete: {
    blurb: "The complete package for buildings where lift reliability is critical.",
    includes: [
      "Everything in Care",
      "Six call-outs a year included, free (worth up to £1,956 at standard rates). Anything beyond that is 25% off, £122.25 instead of £163 for the first hour.",
      "Up to £400 parts cover per lift per year",
    ],
  },
};

// Session ACV: the tier-card copy for a LIFT-LESS covered quote (gates, doors, barriers
// form the plan). Nothing reads "per lift" where no lift exists; parts cover follows the
// covered items. Complete's per-lift six out-of-hours call-out inclusion does not apply on
// this shape (Session ADA, Director decision 12 July 2026: gates-only Complete has no free
// included call-outs; the 25% call-out discount still shows).
export const TIER_CARD_COPY_ITEMS: Record<PublicTierKey, { blurb: string; includes: string[] }> = {
  core: {
    blurb: "Straightforward scheduled servicing, done properly.",
    includes: [
      "Scheduled maintenance visits for your covered items",
      "Full safety compliance on every visit",
      "All labour for routine adjustments",
      "Call-outs and parts at our Schedule of Fees rates",
    ],
  },
  care: {
    blurb: "Servicing plus parts cover and discounted call-outs. Most customers find the parts cover alone justifies the upgrade.",
    includes: [
      "Everything in Core",
      "Up to £250 parts cover per covered item per year",
      `15% off every call-out, ${firstHourFigure("CARE")} instead of ${firstHourFigure("CORE")} for the first hour`,
      // Session AMP, same correction as the per-lift shape above.
      "Minor wear parts included, so small fixes often carry no separate parts charge",
    ],
  },
  complete: {
    blurb: "The complete package for sites where equipment reliability is critical.",
    includes: [
      "Everything in Care",
      "Up to £400 parts cover per covered item per year",
      `25% off any additional call-outs, ${firstHourFigure("COMPLETE")} instead of ${firstHourFigure("CORE")} for the first hour`,
    ],
  },
};

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}
// A figure that can be below zero, written "-£1" rather than gbp's "£-1" (Session APL). Only the "Price adjustment"
// row needs it: the engine rounds each category total on its own (an item at £213.75 shows as £214, a flat item at
// £99.50 as £100) but rounds the price from their sum (£313.25 is £313), so on a quote with no lift row to absorb it
// the difference can be a pound below zero. The "|| 0" folds a negative zero into a plain £0.
function signedGbp(n: number): string {
  const r = Math.round(n) || 0;
  return r < 0 ? `-${gbp(-r)}` : gbp(r);
}
// The modal heads each note "Repairs." with a full stop, so a shared sentence that follows a colon on the PDF opens with a capital here.
function capitalise(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function gbp2(n: number): string {
  return `£${n.toFixed(2)}`;
}
// "£1,400.00": pence AND thousands grouping, for the one figure on this page that crosses a thousand
// and is quoted in prose (the technician visit's special-arrangement day rate, Session AOX; retired by Session TV2, the
// grouping is kept for any figure over a thousand quoted in prose). gbp2
// above deliberately does not group, because it renders into table cells the tests match exactly and
// every figure there is under a thousand; this is its grouped sibling, ICU-independent like the PDF
// templates' own gbp2, rather than a change to gbp2's twelve existing call sites.
function gbp2Grouped(n: number): string {
  const [whole, frac] = n.toFixed(2).split(".");
  return `£${whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${frac}`;
}

// Presentational tier comparison (Session AAU Loop 3): what each plan includes, side by side, with the
// three tier colours (Core green / Care purple / Complete blue). It only reads the prices already on
// the cards; selecting a plan is done on the cards, this table never mutates the choice.
// Session AGZ: the colours are the page's own per-tier TEXT variables (AcceptQuote.css), which
// resolved the pre-existing #00C4FF-vs-#2cb9f3 "Complete blue" inconsistency this constant used
// to carry onto the one tier-text token. Since Session AHD reverted the canvas those variables
// hold the V1 on-white inks on SCREEN as well as in print, so there is no longer a split to
// describe: reading the token is simply how this table stays in step with the tier cards.
const TIER_COLOURS = {
  core: "var(--cq-tier-core-text)",
  care: "var(--cq-tier-care-text)",
  complete: "var(--cq-tier-complete-text)",
} as const;
// Session AKB follow-up: the "Call-out discount" row stated a bare percentage with no base
// figure to compare it against (the same "not a comparable saving" defect AKB fixed on the tier
// cards, left out of scope there for this table). Both cells now state the real locked first-hour
// rate alongside the percentage, £163/£138.55/£122.25 (CALLOUT_FIRST_HOUR in
// server/src/modules/billing/billing.ts, the same source and the same literal figures TIER_CARD_
// COPY above already carries -- the client bundle cannot import server/src at runtime, so these
// are hardcoded literals pinned against the real constant by AcceptQuote.test.tsx, not a second
// implementation). "Included call-outs per lift, per year" is also corrected to "per year":
// allowances.ts's inclusiveCallOutsForTier is a flat six-per-CONTRACT-year allowance, never
// multiplied by lift count, so "per lift" overstated the benefit on any multi-lift Complete
// contract (the same overstatement AKB's own card-copy fix already removed).
// Session RC1 (Director's rules R1/R2, 23 October 2026): the repair rate in the comparison table and the FAQ, read from
// the client mirror of fees.ts (lib/calloutCharge.ts repairHourRate) rather than typed. "Normal hours" is gone because
// the repair evening starts at 18:00, not the call-out card's 17:00, and the non-contract doubling no longer applies.
const REPAIR_LABOUR_CELL = `${gbp2(repairHourRate(false))} weekdays before ${REPAIR_EVENING_START_HOUR}:00, ${gbp2(repairHourRate(true))} at other times`;
const REPAIR_LABOUR_FAQ =
  `Repair labour is charged at ${gbp2(repairHourRate(false))} per hour on weekdays before ${REPAIR_EVENING_START_HOUR}:00 and ${gbp2(repairHourRate(true))} per hour on weekdays from ${REPAIR_EVENING_START_HOUR}:00, at weekends and on bank holidays, whichever plan you hold. ` +
  "The call-out discounts apply to call-out attendance only, and repair work is quoted in advance wherever it is not charged by the hour.";

const TIER_COMPARISON_ROWS: { label: string; core: string; care: string; complete: string }[] = [
  { label: "Planned service visits", core: "Included", care: "Included", complete: "Included" },
  { label: "Parts cover per lift, per year", core: "Not included", care: "Up to £250", complete: "Up to £400" },
  { label: "Call-out discount", core: "Standard rates, £163 first hour", care: "15% off, £138.55 first hour", complete: "25% off, £122.25 first hour" },
  { label: "Included call-outs per year", core: "Not included", care: "Not included", complete: "6 out-of-hours call-outs (worth up to £1,956)" },
  // Session AMP (Director decision, 25 August 2026). The repair rate belongs HERE rather than on the
  // tier cards, and the reason is the table's shape: three columns carrying the same figure is what
  // "flat, whatever tier you hold" looks like at a glance. A per-card line would read as a tier
  // benefit and invite the reader to assume Complete's is cheaper, which is the whole misreading
  // being corrected. "Normal hours" is stated because the out-of-hours and non-contract multipliers
  // still apply to repair labour; they price WHEN the work was done and FOR WHOM.
  {
    label: "Repair labour, per hour",
    core: REPAIR_LABOUR_CELL,
    care: REPAIR_LABOUR_CELL,
    complete: REPAIR_LABOUR_CELL,
  },
];
// Session ACV: the comparison rows for a LIFT-LESS covered quote. Parts cover follows the
// covered items; the per-lift included-call-outs row is omitted (Session ADA, Director
// decision 12 July 2026: gates-only Complete has no free included call-outs, only the 25%
// call-out discount). Call-out discount cells carry the same £ disclosure as the lift-based rows.
const TIER_COMPARISON_ROWS_ITEMS: { label: string; core: string; care: string; complete: string }[] = [
  { label: "Planned service visits", core: "Included", care: "Included", complete: "Included" },
  { label: "Parts cover per covered item, per year", core: "Not included", care: "Up to £250", complete: "Up to £400" },
  { label: "Call-out discount", core: "Standard rates, £163 first hour", care: "15% off, £138.55 first hour", complete: "25% off, £122.25 first hour" },
  // Session AMP (Director decision, 25 August 2026). The repair rate belongs HERE rather than on the
  // tier cards, and the reason is the table's shape: three columns carrying the same figure is what
  // "flat, whatever tier you hold" looks like at a glance. A per-card line would read as a tier
  // benefit and invite the reader to assume Complete's is cheaper, which is the whole misreading
  // being corrected. "Normal hours" is stated because the out-of-hours and non-contract multipliers
  // still apply to repair labour; they price WHEN the work was done and FOR WHOM.
  {
    label: "Repair labour, per hour",
    core: REPAIR_LABOUR_CELL,
    care: REPAIR_LABOUR_CELL,
    complete: REPAIR_LABOUR_CELL,
  },
];
function TierComparison({ tiers, itemsMode = false }: { tiers: { core: number; care: number; complete: number }; itemsMode?: boolean }) {
  const keys = ["core", "care", "complete"] as const;
  const rows = itemsMode ? TIER_COMPARISON_ROWS_ITEMS : TIER_COMPARISON_ROWS;
  return (
    <div className="tier-comparison" aria-label="Plan comparison">
      <h3 className="tier-comparison-title">What each plan includes</h3>
      <div className="tier-comparison-scroll">
        <table className="tier-comparison-table">
          <thead>
            <tr>
              <th></th>
              {keys.map((k) => (
                <th key={k} style={{ color: TIER_COLOURS[k] }}>{TIER_LABEL[k]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                {keys.map((k) => (
                  <td key={k}>{row[k]}</td>
                ))}
              </tr>
            ))}
            <tr className="tier-comparison-price">
              <th scope="row">Annual price (ex VAT)</th>
              {keys.map((k) => (
                <td key={k} style={{ color: TIER_COLOURS[k] }}>{gbp(tiers[k])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The single client-facing phone number, in the locked house format (CLAUDE.md).
// Same number as the V1 header; the canonical spacing keeps the page consistent.
const PHONE = "0203 576 4760";

// ---- ACP customer transparency hooks (Director decision 2.4b). One short line per
// surface, plain English, stating the honest pricing logic -- never a paragraph. ----
// Line 1 FINAL WORDING (Director sign-off, 10 July 2026, Session ACS item B); mirrors
// the server single source (publicQuote.ts FREQUENCY_HONESTY_NOTE), pinned equal below.
export const FREQUENCY_HONESTY_LINE =
  "The more we visit, the less each visit costs - honest pricing for well-maintained lifts.";
export const EQUIPMENT_HONESTY_LINE =
  "Care equipment is priced for maintenance only - one clear price, no tiers, no padding.";

// ---- Session APL: the EQUIPMENT-ONLY page's own words (Director decision D60, 22 September 2026) ----
// An equipment-only quote (no lift, no plan-covered item, at least one care equipment item) has no tier
// and no choice (the ACP spec: "one price, accept"), yet until APL it was headed "Your Lift Maintenance
// Quote", invited the customer to "choose the level of cover", told them to tap "the inside of the lift
// car", claimed BS EN 81 (the lift standard), and itemised "Lift maintenance" in a breakdown named after a
// tier they never chose. Every string below was put to the Director and approved as written. Each is used
// ONLY on that shape; a lift or gates-only page renders exactly as before.
export const EQUIPMENT_ONLY_LEAD = "Your price and servicing schedule are set out below for you to review and accept.";
// The caption under the logo: the quote PDF's own header title ("MAINTENANCE QUOTATION"). The signed
// agreement keeps its own title; that is the agreement's business, not this page's.
export const EQUIPMENT_ONLY_CAPTION = "Maintenance Quotation";
// The equipment RFID page shows the item's service history and its thorough examination strip
// (EquipmentServicePage.tsx), so the line promises exactly those two.
export const EQUIPMENT_RFID_LINE = "Tap your phone on the RFID tag on your equipment to see its service history and examination status.";
// The standards line in the USP band and the trust strip, and the care equipment specification's own
// closing line (serviceSpecification.ts CARE_EQUIPMENT_SPEC_COMPLIANCE_NOTE) say the same thing.
export const EQUIPMENT_STANDARDS = "LOLER 1998, PUWER 1998 and the manufacturer's recommendations";
// Session AQC (Director decision D72): what a GATES-ONLY quote (no lift, a tier-covered gate, door,
// barrier or shutter) works to. BS EN 81 is the lift standard and LOLER governs lifting equipment, so
// neither belongs on a document covering a barrier; PUWER 1998 does. No gate standard is named (BS EN
// 12453 is the usual one) because nobody has said ELE works to it. The server states the same sentence
// on that shape's agreement and quote PDF (serviceSpecification.ts COVERED_ITEMS_SPEC_COMPLIANCE_NOTE).
export const COVERED_ITEMS_STANDARDS = "PUWER 1998 and the manufacturer's recommendations";
// The breakdown's surcharge note on a page with no tier: the client mirror of the server's
// SURCHARGE_PASS_THROUGH_NOTE_SINGLE_PRICE (publicQuote.ts), pinned equal by AcceptQuote.test.tsx.
export const SURCHARGE_PASS_THROUGH_LINE_SINGLE_PRICE =
  "Zone surcharges are statutory and operational cost pass-throughs (congestion charging and controlled parking), charged per planned visit. They are not an East London Elevators charge.";

// The equipment-only page's servicing word (D60), in the equipment's own vocabulary: the strip used to read
// "Twice yearly" beside an item "Serviced six-monthly". The most frequently serviced item decides it,
// because that is how often the site is visited; with no cadenced row (a legacy flat row) the quote's own
// label stands. The quote PDF's equipmentServicingLabel (quote.ts) applies the same rule.
function equipmentServicingWord(items: PublicEquipmentItem[], fallback: string): string {
  let best: { visits: number; label: string } | null = null;
  for (const it of items) {
    const label = String(it.frequencyLabel ?? "").trim();
    const visits = Number(it.visitsYr);
    if (label === "" || !Number.isFinite(visits)) continue;
    if (!best || visits > best.visits) best = { visits, label };
  }
  return best ? best.label : fallback;
}

// The GATES-ONLY page's servicing word (Session AQC, D72). The strip read the quote's summary label,
// "Twice yearly", directly above a covered row saying "Serviced six-monthly": the same mismatch APL
// fixed for care equipment. A covered row carries its cadence as a LABEL and no visit count, so there
// is nothing to take a maximum of: the row's own word is used when the covered items agree on one, and
// with several different cadences the quote's own summary label stands rather than a guess.
function coveredServicingWord(rows: { frequencyLabel?: string | null }[] | undefined, fallback: string): string {
  const labels = new Set((rows ?? []).map((r) => String(r.frequencyLabel ?? "").trim()).filter((l) => l !== ""));
  return labels.size === 1 ? [...labels][0]! : fallback;
}

// The row's annual for a care-equipment line: the server-computed tapered annual when
// present (ACP), else the flat price x qty (legacy rows and stale bundles).
function equipmentItemAnnual(it: PublicEquipmentItem): number {
  return Number.isFinite(it.annual as number) ? (it.annual as number) : it.price * it.qty;
}

// The serviced-cadence word for the equipment block's plain-English line ("serviced
// six-monthly"). null when the row has no cadence (a legacy flat row).
function equipmentServicedWord(it: PublicEquipmentItem): string | null {
  const label = it.frequencyLabel ?? null;
  return label ? label.toLowerCase() : null;
}

// The visit frequencies a customer may choose (Session AAG; restored to the full set --
// bi_monthly included -- Session AHU). Built from the shared frequencyOptionsForType
// (src/lib/quotePricing.ts), the same source the office calculator uses, rather than a
// second hardcoded list: it was exactly that duplicate list, frozen at Session AAG, that
// let this page silently drop bi_monthly when Decision 2.2a (10 July 2026) repriced and
// kept it as a valid customer-selectable frequency.
// Annual is filtered out entirely rather than shown disabled: it is never customer-
// selectable (office-set for goods lift types only, BS EN 81-28 / LOLER). This differs
// from the office calculator's own presentation of frequencyOptionsForType, which shows
// Annual greyed out with a reason rather than omitting it -- appropriate for an office user
// choosing a lift type, not for this page, which never offers Annual regardless of type.
// The public PublicLift DTO carries no raw liftType key (only a free-text display string,
// `lift.type`), so the compliance guard's actual per-type distinction cannot be reproduced
// here; that is harmless for this page because the guard's only lift-type-dependent
// behaviour is the Annual disable, which this page discards unconditionally, so every
// other frequency is returned unchanged regardless of what is passed as liftType. Changing
// a frequency reprices the DISPLAY via the server; the signed agreement keeps the
// office-set frequency until the office confirms the change.
function customerFreqOptions(liftType: string | undefined): FrequencyOption[] {
  return frequencyOptionsForType(liftType ?? "").filter((o) => o.value !== "annual");
}
function coerceCustomerFreq(liftType: string | undefined, f: string): string {
  return coerceFrequencyForType(liftType ?? "", f as QuoteFrequency);
}

// The plain-English spec line under each lift name in the Visit frequency section: type, capacity
// and the currently chosen visit frequency, middot-separated (a blank spec field is dropped). Keeps
// the customer oriented on what each lift is while they set its schedule and read its price.
function liftSpecLine(lift: PublicLift, freqValue: string): string {
  return [lift.type, lift.capacity, frequencyLabel(freqValue as QuoteFrequency)].map((s) => (s ?? "").trim()).filter(Boolean).join(" · ");
}

// The per-lift annual price for the given tier, or null when the quote carries no per-lift pricing
// (a legacy quote) or no tier is selected yet. Read straight from the server-computed annualByTier;
// the component never prices anything itself.
function liftAnnualFor(lift: PublicLift, tier: PublicTierKey | null): number | null {
  if (!tier || !lift.annualByTier) return null;
  const v = lift.annualByTier[tier];
  return Number.isFinite(v) ? v : null;
}

// The per-lift documentation fields shown in the Lift specification section (Session AAK),
// in the V1 display order. Each is a ready-to-render display string from the server; a blank
// value means "omit this field" (matching V1, where an optional field like the serial number
// is hidden when blank). Frequency is deliberately not here (the interactive selector below
// covers it). Type is always populated; the rest are optional on the DTO.
const LIFT_SPEC_FIELDS: { label: string; get: (l: PublicLift) => string | undefined }[] = [
  { label: "Type", get: (l) => l.type },
  { label: "Capacity", get: (l) => l.capacity },
  { label: "Age", get: (l) => l.age },
  { label: "Stops", get: (l) => l.stops },
  { label: "Manufacturer", get: (l) => l.manufacturer },
  { label: "Serial number", get: (l) => l.serial },
  { label: "Controller", get: (l) => l.controller },
  { label: "Door type", get: (l) => l.door },
  { label: "Drive system", get: (l) => l.driveSystem },
  { label: "Motor room location", get: (l) => l.motorRoom },
];

// The Lift specification section: what the customer is being quoted to maintain (type, age,
// stops, serial, manufacturer, ...). A key-value table for a single lift; a column-per-lift
// grid for several. A field is shown only when at least one lift has a value for it, so blank
// fields are dropped cleanly. Renders nothing when no lift detail was captured (a legacy quote,
// or a stale API shape without the spec fields), so the page degrades gracefully.
function LiftSpecSection({ lifts }: { lifts: PublicLift[] }) {
  const rows = LIFT_SPEC_FIELDS.filter((f) => lifts.some((l) => (f.get(l) ?? "") !== ""));
  if (lifts.length === 0 || rows.length === 0) return null;

  if (lifts.length === 1) {
    const l = lifts[0]!;
    const namedLift = l.label && !/^Lift \d+$/.test(l.label) ? l.label : "";
    return (
      <div className="sec">
        <div className="sec-title">Lift specification</div>
        <table className="liftspec-kv">
          <tbody>
            {namedLift ? (
              <tr>
                <td className="k">Lift</td>
                <td>{namedLift}</td>
              </tr>
            ) : null}
            {rows.map((f) => {
              const v = f.get(l) ?? "";
              return v ? (
                <tr key={f.label}>
                  <td className="k">{f.label}</td>
                  <td>{v}</td>
                </tr>
              ) : null;
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="sec">
      <div className="sec-title">Lift specification</div>
      <div className="liftspec-scroll">
        <table className="liftspec-grid">
          <thead>
            <tr>
              <th className="k">Specification</th>
              {lifts.map((l, i) => (
                <th key={i}>{l.label || `Lift ${i + 1}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.label}>
                <td className="k">{f.label}</td>
                {lifts.map((l, i) => (
                  <td key={i}>{f.get(l) ?? ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The multi-lift discount banner (Session AIV): states the discount plainly above the tier cards
// whenever one genuinely applies, so the accept page and the quote PDF (which has always stated
// this as "Per-lift prices shown include the X% multi-lift discount") agree. Guarded on
// numLifts > 0: equipment and tier-covered items are never multi-lift discounted (pricing.ts), so
// a lift-less quote's discountBasis, even "portfolio" (earned by OTHER quotes in the customer's
// grouped portfolio), discounts nothing on THIS quote -- stating a percentage would be misleading.
function DiscountBanner({
  numLifts,
  discountLiftCount,
  discountBasis,
  multiLiftDiscountPct,
}: {
  numLifts: number;
  discountLiftCount: number;
  discountBasis: PublicQuote["discountBasis"];
  multiLiftDiscountPct: number;
}) {
  if (multiLiftDiscountPct <= 0 || numLifts <= 0) return null;
  const text =
    discountBasis === "portfolio" && discountLiftCount > numLifts
      ? `This is a ${numLifts}-lift quote, priced as part of your wider ${discountLiftCount}-lift portfolio with East London Elevators. A ${multiLiftDiscountPct}% multi-lift discount has been applied as a result.`
      : `A ${multiLiftDiscountPct}% multi-lift discount has been applied because this quote covers ${numLifts} lift${numLifts === 1 ? "" : "s"}.`;
  return <div className="discount-banner">{text}</div>;
}

export function AcceptQuote() {
  const { token = "" } = useParams();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "error" | null>(null);
  const [selected, setSelected] = useState<PublicTierKey | null>(null);
  // Session AAG: the customer's chosen per-lift frequency, and the live-reprice state. The prices
  // shown reprice from the server on change (no client-side pricing); acceptance sends these for the
  // office to confirm, but the agreement is prepared at the office-set frequency.
  const [frequencies, setFrequencies] = useState<string[]>([]);
  // The frequencies the office quoted, captured on load and never repriced, so accept can tell
  // whether the customer actually requested a different schedule (only then does the office need to
  // confirm a change).
  const [initialFrequencies, setInitialFrequencies] = useState<string[]>([]);
  const [repricing, setRepricing] = useState(false);
  const [freqError, setFreqError] = useState<string | null>(null);
  // Session ACF: the full lift list captured on load (never repriced away), so the include checkboxes
  // stay stable even after a coverage reprice returns only the included lifts. `included[i]` tracks
  // whether the customer keeps lift i on the contract; excluded lifts are repriced OUT and become
  // uncontracted lifts the office can add later.
  const [allLifts, setAllLifts] = useState<PublicLift[]>([]);
  const [included, setIncluded] = useState<boolean[]>([]);
  // The inclusion set the CURRENT `quote` was priced from. `included` updates optimistically on a tick,
  // but the repriced `quote.lifts` only arrives after the round-trip; mapping per-lift prices against
  // this (not `included`) means a lift never briefly shows another lift's price during a reprice.
  const [pricedIncluded, setPricedIncluded] = useState<boolean[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Session AAN: the customer's optional preferred start date ("YYYY-MM-DD"). Sent on accept; shown
  // on the ESA as the commencement, with the actual start being the later of it and the signing date.
  const [preferredCommencement, setPreferredCommencement] = useState("");
  // Session AUJ: "Is someone else signing?" Default "No, I'll sign", which sends nothing.
  const [signatoryAnswer, setSignatoryAnswer] = useState<SignatoryAnswer>(NO_SIGNATORY);
  // Once accepted, drive the confirmation view from these so it shows instantly.
  const [accepted, setAccepted] = useState<{ tier: PublicTierKey; message: string } | null>(null);
  // Item 4D: which info modal is open (Terms, Maintenance Schedule or Schedule of Fees, Session
  // AKR), or none.
  const [modal, setModal] = useState<"terms" | "maintenance" | "fees" | null>(null);
  // Today (local) as YYYY-MM-DD, the min for the preferred-start-date picker (Session AAN).
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.publicQuote
      .get(token)
      .then((q) => {
        if (!live) return;
        setQuote(q);
        setSelected(q.recommendedTier);
        {
          const initial = (q.lifts ?? []).map((l) => coerceCustomerFreq(l.type, l.frequency));
          setFrequencies(initial);
          setInitialFrequencies(initial);
          // Session ACF: capture the full lift list + default every lift to included.
          setAllLifts(q.lifts ?? []);
          setIncluded((q.lifts ?? []).map(() => true));
          setPricedIncluded((q.lifts ?? []).map(() => true));
        }
        if (q.status === "accepted") {
          setAccepted({ tier: q.acceptedTier ?? q.recommendedTier, message: "" });
        }
      })
      .catch((err) => {
        if (!live) return;
        setLoadError(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const onAccept = useCallback(async () => {
    if (!selected || submitting) return;
    // Session AUJ: an incomplete "someone else is signing" answer is caught before anything is sent.
    const signing = signatoryToSend(signatoryAnswer);
    if (signing.error) {
      setSubmitError(signing.error);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Only send the frequencies when the customer changed one from what was quoted, so the office
      // is only asked to confirm a genuine change; an unchanged accept posts just the tier. The
      // preferred start date is sent whenever the customer picked one (Session AAN). Trailing optional
      // arguments are omitted when empty so the common accept keeps its minimal call shape.
      const changed = frequencies.length > 0 && frequencies.some((f, i) => f !== initialFrequencies[i]);
      const freqArg = changed ? frequencies : undefined;
      const pref = preferredCommencement.trim() || undefined;
      // Session ACF: send the included-lift indexes only when the customer actually excluded a lift
      // (a strict subset); an all-included accept keeps the minimal call shape. Trailing optional
      // arguments are omitted when empty so the common accept posts just the tier.
      const includedIndexes = included.flatMap((v, i) => (v ? [i] : []));
      const inclArg = includedIndexes.length > 0 && includedIndexes.length < allLifts.length ? includedIndexes : undefined;
      const res = await (signing.signatory
        ? api.publicQuote.accept(token, selected, freqArg, pref, inclArg, signing.signatory)
        : inclArg
        ? api.publicQuote.accept(token, selected, freqArg, pref, inclArg)
        : pref
          ? api.publicQuote.accept(token, selected, freqArg, pref)
          : freqArg
            ? api.publicQuote.accept(token, selected, freqArg)
            : api.publicQuote.accept(token, selected));
      // Item 5 (Session ACB): the confirmation screen must name the tier the CUSTOMER selected,
      // which is exactly `selected` (what the Accept button showed). Do not read the tier off the
      // server response here: the confirmation previously always showed the same tier regardless of
      // choice. `selected` is guaranteed set (the guard at the top of onAccept).
      setAccepted({ tier: selected, message: res.message });
    } catch (err) {
      const generic = `Something went wrong. Please try again, or call the office on ${PHONE}.`;
      if (err instanceof ApiError && err.status === 410) {
        // Expired between load and submit: switch to the expired view (which has
        // its own copy), so do not also set an inline error that cannot render.
        setSubmitError(null);
        setQuote((q) => (q ? { ...q, status: "expired" } : q));
      } else if (err instanceof ApiError && err.status === 404) {
        setSubmitError(`We could not find this quote. It may have already been used. Please call the office on ${PHONE}.`);
      } else if (err instanceof ApiError && err.status === 429) {
        setSubmitError(`Please wait a moment and try again, or call the office on ${PHONE}.`);
      } else if (err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")) {
        // The server attached a plain-English message (409/500 paths); only ever
        // surface server text that reads like a sentence, never a machine code.
        setSubmitError(err.code);
      } else {
        setSubmitError(generic);
      }
    } finally {
      setSubmitting(false);
    }
  }, [selected, submitting, token, frequencies, initialFrequencies, preferredCommencement, included, allLifts.length, signatoryAnswer]);

  // Reprice the DISPLAY from the server (authoritative; no client-side pricing) for the current
  // frequencies + inclusion. When lifts are excluded the included subset is repriced at its true
  // discount bracket. `revert` restores the caller's optimistic state on failure so the controls
  // always match the price on screen. Session AAG (frequency) + Session ACF (inclusion).
  const doReprice = useCallback(
    async (freqs: string[], incl: boolean[], revert: () => void) => {
      const includedIndexes = incl.flatMap((v, i) => (v ? [i] : []));
      setFreqError(null);
      setRepricing(true);
      try {
        const repriced =
          includedIndexes.length === allLifts.length
            ? await api.publicQuote.preview(token, freqs)
            : await api.publicQuote.preview(token, freqs, includedIndexes);
        setQuote(repriced);
        setPricedIncluded(incl); // the inclusion set this repriced view corresponds to
      } catch (err) {
        revert();
        // Session ACX: this used to show a fixed line regardless of what the server actually
        // rejected (a lift-count mismatch, an invalid frequency, or a pricing failure all looked
        // identical to the customer AND to whoever was debugging it). The server's reason lives at
        // details.frequencies[0] (see ApiError, and repriceRejectionReason server-side) -- show it
        // when present and it reads like a sentence, exactly the same guard already used for
        // setSubmitError above, so a raw error code never leaks onto the customer's screen.
        const reason = err instanceof ApiError ? err.details?.frequencies?.[0] : undefined;
        setFreqError(
          reason && reason.includes(" ")
            ? reason
            : `We could not update the price just now. Please try again, or call the office on ${PHONE}.`,
        );
      } finally {
        setRepricing(false);
      }
    },
    [token, allLifts.length],
  );

  // The customer changed a lift's visit frequency (Session AAG).
  const changeFrequency = useCallback(
    (index: number, value: string) => {
      const prev = frequencies;
      const next = prev.map((f, i) => (i === index ? value : f));
      setFrequencies(next);
      void doReprice(next, included, () => setFrequencies(prev));
    },
    [frequencies, included, doReprice],
  );

  // The customer included/excluded a lift from the contract (Session ACF). A contract must keep at
  // least one lift, so the last remaining tick cannot be cleared.
  const toggleInclude = useCallback(
    (index: number) => {
      const next = included.map((v, i) => (i === index ? !v : v));
      if (next.every((v) => !v)) {
        setFreqError("Please keep at least one lift on your contract.");
        return;
      }
      const prev = included;
      setIncluded(next);
      void doReprice(frequencies, next, () => setIncluded(prev));
    },
    [included, frequencies, doReprice],
  );

  const orderedTiers = useMemo<PublicTierKey[]>(() => ["core", "care", "complete"], []);

  // The loading state matches the page it becomes (the light document shell), never a flash
  // of the dark app loader (Session AAV, Loop 3).
  if (loading) {
    return (
      <Shell>
        <div className="sec notice" role="status" aria-live="polite">
          <h2>Loading your quote</h2>
          <p>Just a moment.</p>
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div className="sec notice">
          <h2>{loadError === "not_found" ? "We could not find this quote" : "Something went wrong"}</h2>
          <p>
            {loadError === "not_found"
              ? "This link may have expired or already been used. Please call the office and we will help."
              : "Please try again in a moment, or call the office and we will help."}
          </p>
          <p className="call">Call us on {PHONE}</p>
        </div>
      </Shell>
    );
  }

  if (!quote) return null;

  // Belt and braces on the PUBLIC page: the server always sends these (legacy payloads
  // map to an empty list), but a stale bundle/API overlap during a deploy must degrade
  // to "no surcharge lines", never a blank page.
  const surcharges = quote.surcharges ?? [];
  const surchargeAnnualTotal = quote.surchargeAnnualTotal ?? 0;

  // ---- Session ACS: the additional-items tier toggle on the customer page ----
  // A quote with at least one TIER-COVERED item renders the plan-centred shape: the tier
  // cards show each plan's maintenance figure (which includes its own covered items, so
  // the plans genuinely differ), the flat (tiers-off) items become a separate
  // always-included block, and the breakdown itemises the covered items at the selected
  // plan's figure. A quote with NO covered items -- every legacy quote -- renders
  // EXACTLY as before, so no already-sent quote changes shape or numbers.
  const coveredRows = quote.additionalCovered?.rows ?? [];
  const hasCovered = coveredRows.length > 0;
  const coveredForSelected = selected
    ? coveredRows.map((r) => ({ description: r.description, qty: r.qty, annual: r.annual[selected], frequencyLabel: r.frequencyLabel ?? null }))
    : [];
  const coveredTotalForSelected = selected && quote.additionalCovered ? quote.additionalCovered.totals[selected] : 0;

  // ---- The page shapes (ACP spec 2.4; Session ACV adds the gates-only tiered fork) ----
  // Lift present: tier cards as today (covered items inside the plans; equipment its own
  // always-included block). No lift + at least one COVERED item ("itemsMode"): the tier
  // cards render too -- the ITEMS form the plan -- with every per-lift benefit line
  // generalised to "per covered item" (nothing reads "per lift" where no lift exists) and
  // Complete's per-lift out-of-hours inclusion omitted (Session ADA, Director decision 12
  // July 2026: gates-only Complete has no free included call-outs, only the 25% call-out
  // discount). No lift + no covered item: the equipment-only single-price shape, unchanged.
  const hasEquipment = (quote.careHomeItems ?? []).length > 0;
  const itemsMode = quote.numLifts === 0 && hasCovered;
  const equipmentOnly = hasEquipment && quote.numLifts === 0 && !hasCovered;

  // The always-included care-equipment block: each item with its serviced cadence and
  // annual, one total, and the honesty line (spec 2.4). On an equipment-only quote this
  // IS the price block the customer accepts.
  const equipmentSection = hasEquipment ? (
    <div className="sec">
      <div className="sec-title">{equipmentOnly ? "Your care equipment maintenance" : "Care equipment (always included)"}</div>
      {!equipmentOnly && (
        <p className="sec-note">
          {/* Session ACV: on a lift-less covered quote the equipment rides the covered
              items' visits, never "the lifts" (there are none). */}
          {itemsMode
            ? "Your care equipment is maintained alongside your covered items. It has one price whichever plan you choose above."
            : "Your care equipment is maintained alongside the lifts. It has one price whichever plan you choose above."}
        </p>
      )}
      <div className="ibd">
        {quote.careHomeItems.map((it, i) => {
          const serviced = equipmentServicedWord(it);
          return (
            <div className="ibd-row" key={i}>
              <div className="ibd-nm">
                {it.description}
                {it.qty > 1 ? ` x ${it.qty}` : ""}
                {serviced ? (
                  <ul className="ibd-sub">
                    <li>Serviced {serviced}</li>
                  </ul>
                ) : null}
              </div>
              <div className="ibd-c">{gbp(equipmentItemAnnual(it))}</div>
            </div>
          );
        })}
        <div className="ibd-row ibd-total">
          <div className="ibd-nm">Care equipment maintenance</div>
          <div className="ibd-c">{gbp(quote.careHomeTotal)} / year ex VAT</div>
        </div>
      </div>
      <p className="fineprint">{EQUIPMENT_HONESTY_LINE}</p>
    </div>
  ) : null;

  // Session ACS: the always-included FLAT additional items on a covered quote -- the
  // same separated treatment the equipment block uses, clearly labelled and distinct
  // from care equipment. Rendered ONLY when the quote also carries tier-covered items;
  // with none (every legacy quote) the flat items keep their price-breakdown row
  // exactly as before, so no already-sent quote changes shape.
  const additionalFlatSection =
    hasCovered && quote.additionalItems.length > 0 ? (
      <div className="sec">
        <div className="sec-title">Additional items (always included)</div>
        <p className="sec-note">
          These items are maintained at one flat price whichever plan you choose. They are maintenance-only: parts
          allowances and plan benefits do not apply to them.
        </p>
        <div className="ibd">
          {quote.additionalItems.map((it, i) => (
            <div className="ibd-row" key={i}>
              <div className="ibd-nm">
                {it.description}
                {it.qty > 1 ? ` x ${it.qty}` : ""}
              </div>
              <div className="ibd-c">{gbp(it.price * it.qty)}</div>
            </div>
          ))}
          <div className="ibd-row ibd-total">
            <div className="ibd-nm">Additional items</div>
            <div className="ibd-c">{gbp(quote.additionalItemsTotal)} / year ex VAT</div>
          </div>
        </div>
      </div>
    ) : null;

  // Item 3 (Session ACB): the Visit frequency selectors were extracted so they render BELOW the
  // tier cards (the customer chooses cover first, then how often). Item 4: the section wraps in
  // .cq-freq-sec so the per-lift rows share the tier cards' interactive visual language. The live
  // reprice is unchanged (a frequency change calls the server; a tier change re-reads stored prices).
  // Session ACF: the repriced view returns only the INCLUDED lifts (in included order), so map each
  // stable full-list lift back to its repriced per-lift price by its position among the included lifts.
  // Keyed on pricedIncluded (the set the CURRENT quote was priced from), never the optimistic
  // `included`, so a lift never briefly shows another lift's price mid-reprice.
  const pricedIndexes = pricedIncluded.flatMap((v, i) => (v ? [i] : []));
  const priceForIndex = (i: number): number | null => {
    if (!pricedIncluded[i]) return null;
    const pos = pricedIndexes.indexOf(i);
    const repriced = quote.lifts[pos];
    return repriced ? liftAnnualFor(repriced, selected) : null;
  };
  const anyExcluded = included.some((v) => !v);

  const frequencySection =
    allLifts.length > 0 ? (
      <div className="sec cq-freq-sec">
        <div className="sec-title">Visit frequency</div>
        {/* The interactive selectors are hidden in print; the chosen frequencies print as a
            static line instead, so a saved copy still records the basis of its prices. */}
        <div className="cq-noprint">
          <p className="sec-note">
            You have chosen your level of cover above. Now choose how often each lift is serviced and the prices update
            instantly. Your quotation is based on the frequency shown; if you would like a different schedule, select it
            and accept, and we will confirm the final schedule and price with you before preparing your agreement.
          </p>
          {allLifts.length > 1 && (
            <p className="sec-note">
              If a lift is still under another provider's contract, untick it below to leave it off this agreement. The
              price updates to cover only the lifts you keep, and we can add the others later once you are ready.
            </p>
          )}
          <div className="cq-liftrows">
            {allLifts.map((lift, i) => {
              const freqValue = frequencies[i] ?? coerceCustomerFreq(lift.type, lift.frequency);
              const price = priceForIndex(i);
              const isIncluded = included[i] ?? true;
              return (
                <div key={i} className={`cq-liftrow${isIncluded ? "" : " cq-liftrow-excluded"}`}>
                  <div className="cq-liftrow-info">
                    {allLifts.length > 1 && (
                      <label className="cq-liftrow-include">
                        <input
                          type="checkbox"
                          checked={isIncluded}
                          disabled={repricing}
                          onChange={() => toggleInclude(i)}
                        />
                        <span>Include this lift in my contract</span>
                      </label>
                    )}
                    <label htmlFor={`freq-${i}`} className="cq-liftrow-name">
                      {lift.label}
                    </label>
                    <div className="cq-liftrow-spec">{liftSpecLine(lift, freqValue)}</div>
                  </div>
                  <div className="cq-liftrow-controls">
                    <select
                      id={`freq-${i}`}
                      value={freqValue}
                      disabled={repricing || !isIncluded}
                      onChange={(e) => changeFrequency(i, e.target.value)}
                      className="cq-liftrow-select"
                    >
                      {customerFreqOptions(lift.type).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {isIncluded ? (
                      price !== null && (
                        <div className="lift-price-line" data-testid={`liftprice-${i}`}>
                          <span className="lift-price-amt">{gbp(price)}</span>
                          <span className="lift-price-unit">/ year ex VAT</span>
                        </div>
                      )
                    ) : (
                      <div className="lift-price-line" data-testid={`liftexcluded-${i}`}>
                        <span className="lift-price-unit">Not included</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {selected && quote.lifts.some((l) => liftAnnualFor(l, selected) !== null) && (
            <p className="sec-note cq-liftprice-note">
              Each price is that lift's own maintenance on the {TIER_LABEL[selected]} plan. Your whole-site total is
              shown in the plans above{anyExcluded ? ", and covers only the lifts you have ticked" : ""}.
            </p>
          )}
          {repricing && (
            <p className="sec-note" aria-live="polite">
              Updating prices...
            </p>
          )}
          {freqError && (
            <p role="alert" className="cq-error">
              {freqError}
            </p>
          )}
          {/* ACP transparency hook (Director decision 2.4b): one line under the frequency
              chooser stating the honest pricing logic. Wording pending Director sign-off. */}
          <p className="sec-note">{FREQUENCY_HONESTY_LINE}</p>
        </div>
        <p className="cq-print-freqs">
          {allLifts
            .map((lift, i) => {
              const value = frequencies[i] ?? coerceCustomerFreq(lift.type, lift.frequency);
              const label = frequencyLabel(value as QuoteFrequency);
              if (!(included[i] ?? true)) return `${lift.label}: not included`;
              const price = priceForIndex(i);
              return price !== null ? `${lift.label}: ${label} (${gbp(price)}/year)` : `${lift.label}: ${label}`;
            })
            .join(" · ")}
        </p>
      </div>
    ) : null;

  return (
    <Shell caption={equipmentOnly ? EQUIPMENT_ONLY_CAPTION : undefined}>
      <div className="intro">
        {/* Session ACV: a lift-less covered quote is for gates/doors/barriers, so the
            heading never claims lift maintenance. Session APL (D60): nor does an equipment-only one, which
            also has no level of cover to choose. Every other shape is unchanged. */}
        <h1>{itemsMode || equipmentOnly ? "Your Maintenance Quote" : "Your Lift Maintenance Quote"}</h1>
        <p className="lead">
          {equipmentOnly
            ? `Thank you for considering East London Elevators. ${EQUIPMENT_ONLY_LEAD}`
            : "Thank you for considering East London Elevators. Choose the level of cover that suits you and confirm your selection below."}
          {quote.validUntilDisplay ? ` This quotation is valid until ${quote.validUntilDisplay}.` : ""}
        </p>
      </div>

      <DiscountBanner
        numLifts={quote.numLifts}
        discountLiftCount={quote.discountLiftCount}
        discountBasis={quote.discountBasis}
        multiLiftDiscountPct={quote.multiLiftDiscountPct}
      />

      <div className="strip">
        <div className="strip-cell">
          <div className="k">Prepared for</div>
          <div className="v">{quote.customerName}</div>
        </div>
        <div className="strip-cell">
          <div className="k">Site</div>
          <div className="v">{quote.siteName || "To be confirmed"}</div>
          {quote.siteAddress ? <div className="s">{quote.siteAddress}</div> : null}
        </div>
        <div className="strip-cell">
          <div className="k">Reference</div>
          <div className="v">{quote.quoteRef}</div>
          <div className="s">Prepared {quote.dateDisplay}</div>
        </div>
        <div className="strip-cell">
          <div className="k">Servicing</div>
          <div className="v">
            {equipmentOnly
              ? equipmentServicingWord(quote.careHomeItems, quote.frequencyLabel)
              : itemsMode
                ? coveredServicingWord(quote.additionalCovered?.rows, quote.frequencyLabel)
                : quote.frequencyLabel}
          </div>
          <div className="s">
            {equipmentOnly
              ? `${quote.careHomeItems.length} care equipment item${quote.careHomeItems.length === 1 ? "" : "s"}`
              : itemsMode
                ? `${coveredRows.length} plan-covered item${coveredRows.length === 1 ? "" : "s"}`
                : `${quote.numLifts} lift${quote.numLifts === 1 ? "" : "s"}`}
          </div>
        </div>
      </div>

      <LiftSpecSection lifts={quote.lifts} />

      <UspBanner itemsMode={itemsMode} equipmentOnly={equipmentOnly} />

      {accepted ? (
        <AcceptedPanel tier={accepted.tier} message={accepted.message} equipmentOnly={equipmentOnly} />
      ) : quote.status === "expired" ? (
        <div className="sec notice">
          <h2>This quotation has expired</h2>
          <p>Please call the office for an up-to-date quote and we will sort it out straight away.</p>
          <p className="call">Call us on {PHONE}</p>
        </div>
      ) : (
        <>
          {equipmentOnly ? (
            /* ACP equipment-only shape (spec 2.4): NO tier choice step at all. One price
               block (the equipment section below), then accept. The promo banner still
               applies -- the promotional rate covers the whole quote (decision 2.4a). */
            <div className="sec">
              <div className="sec-title">Your price</div>
              <p className="sec-note">
                {/* Session AQC (D72): "only" is not true when a flat item is priced on the same quote. */}
                {quote.additionalItems.length > 0
                  ? "This quotation is for care equipment maintenance and the additional items listed below."
                  : "This quotation is for care equipment maintenance only."}{" "}
                There are no tiers to choose: one clear price for the servicing schedule shown below.
              </p>
              {quote.promo && (
                <div className="promo">
                  <b>Introductory offer{quote.promo.reason ? `: ${quote.promo.reason}` : ""}.</b> {quote.promo.pct}% off for
                  the first {quote.promo.months} months.
                </div>
              )}
            </div>
          ) : (
          <div className="sec">
            <div className="sec-title">Choose your cover</div>
            <p className="sec-note">
              Select a plan.{" "}
              {itemsMode
                ? "Each plan's price covers the plan-covered items itemised in the breakdown below; any other extras are priced separately and included whichever plan you choose."
                : hasCovered
                  ? "Each plan's price covers your lifts and the plan-covered items itemised in the breakdown below; your other extras are priced separately and included whichever plan you choose."
                  : hasEquipment
                    ? "The plan price covers your lifts; your care equipment is priced separately below and is included whichever plan you choose."
                    : "The price shown is the annual maintenance value for your whole site."}
            </p>

            {quote.promo && (
              <div className="promo">
                <b>Introductory offer{quote.promo.reason ? `: ${quote.promo.reason}` : ""}.</b> {quote.promo.pct}% off for
                the first {quote.promo.months} months.
              </div>
            )}

            <fieldset className="tier-grid">
              <legend className="sr-only">Choose a maintenance plan</legend>
              {orderedTiers.map((key) => (
                <TierCard
                  key={key}
                  tierKey={key}
                  tier={quote.tiers[key]}
                  recommended={key === quote.recommendedTier}
                  selected={selected === key}
                  onSelect={() => setSelected(key)}
                  surcharges={surcharges}
                  hasEquipment={hasEquipment}
                  careHomeTotal={quote.careHomeTotal}
                  planMode={hasCovered}
                  itemsMode={itemsMode}
                />
              ))}
            </fieldset>

            <p className="tier-note-once">
              All prices exclude VAT. Care and Complete include the call-out discounts shown. Invoices are issued 30 days
              before each scheduled service.
            </p>

            {/* Session AAU Loop 3: presentational tier comparison. It reads the same prices already
                shown on the cards and never changes the selected tier. Core green / Care purple /
                Complete blue. */}
            <TierComparison
              tiers={{ core: quote.tiers.core.annual, care: quote.tiers.care.annual, complete: quote.tiers.complete.annual }}
              itemsMode={itemsMode}
            />
          </div>
          )}

          {/* Item 3 (Session ACB): frequency selectors sit directly below the tier cards, the logical
              decision order (choose cover, then set how often). */}
          {frequencySection}

          {/* ACP: the always-included care-equipment block (mixed quotes), or THE price
              block (equipment-only quotes). */}
          {equipmentSection}

          {/* Session ACS: the always-included flat additional items (covered quotes only;
              legacy quotes keep their price-breakdown row exactly as before). */}
          {additionalFlatSection}

          {/* One tier-free narrative, shown for every tier (Session AA, Item 5): it describes ELE
              and the service, never a tier, so it does not change when the customer switches tier. */}
          {quote.quoteNarrative ? <QuoteNarrative text={quote.quoteNarrative} /> : null}
          {quote.quoteExplanation ? <QuoteExplanation text={quote.quoteExplanation} /> : null}

          {selected && (quote.additionalItems.length > 0 || quote.careHomeItems.length > 0 || surcharges.length > 0 || hasCovered) && (
            <PriceBreakdown
              tierLabel={TIER_LABEL[selected]}
              tier={quote.tiers[selected]}
              additionalItems={quote.additionalItems}
              careHomeItems={quote.careHomeItems}
              additionalItemsTotal={quote.additionalItemsTotal}
              careHomeTotal={quote.careHomeTotal}
              surcharges={surcharges}
              surchargeAnnualTotal={surchargeAnnualTotal}
              coveredItems={coveredForSelected}
              coveredTotal={coveredTotalForSelected}
              itemsMode={itemsMode}
              equipmentOnly={equipmentOnly}
            />
          )}

          <div className="sec summary" id="cq-confirm">
            {/* Session APL (D60): an equipment-only quote has nothing to select. */}
            <div className="sec-title">{equipmentOnly ? "Accept your quote" : "Confirm your selection"}</div>
            {selected && (
              <div className="sel-box">
                {/* Equipment-only quotes have no plan to name (spec 2.4: one price, accept). */}
                {!equipmentOnly && (
                  <div className="sel-line">
                    <span className="sel-k">Selected plan</span>
                    <span className="sel-v">{TIER_LABEL[selected]}</span>
                  </div>
                )}
                <div className="sel-line">
                  <span className="sel-k">Annual</span>
                  <span className="sel-v">{gbp(quote.tiers[selected].annual)} + VAT / year</span>
                </div>
              </div>
            )}

            <div className="cq-commence">
              <label htmlFor="cq-preferred-start">Preferred start date (optional)</label>
              <input
                id="cq-preferred-start"
                type="date"
                value={preferredCommencement}
                min={todayIso}
                onChange={(e) => setPreferredCommencement(e.target.value)}
              />
              <p className="cq-commence-note">
                Leave blank and we will propose a start date. Your agreement begins on your preferred date or the date
                it is signed, whichever is later.
              </p>
            </div>

            <SignatoryQuestion customerName={quote.customerName} value={signatoryAnswer} onChange={setSignatoryAnswer} disabled={submitting} />

            {submitError && (
              <p role="alert" aria-live="assertive" className="cq-error">
                {submitError}
              </p>
            )}

            <button type="button" onClick={onAccept} disabled={submitting || !selected} className="confirm">
              {submitting ? "Confirming..." : equipmentOnly ? "Accept quote" : `Accept ${selected ? TIER_LABEL[selected] : ""}`}
            </button>
            <div className="cq-modal-buttons">
              {/* Session APD: the standard terms only for a customer who signs the Standard ESA
                  (standardTermsApplyTo, below). The row is a flex row, so two buttons fill it. */}
              {standardTermsApplyTo(quote) ? (
                <button type="button" className="cq-info-btn" onClick={() => setModal("terms")}>
                  Terms and Conditions
                </button>
              ) : null}
              <button type="button" className="cq-info-btn" onClick={() => setModal("maintenance")}>
                Maintenance Schedule
              </button>
              {/* Session AKR: an in-page modal, matching Terms and Conditions / Maintenance
                  Schedule (Director-confirmed gap: Session AKD's plain PDF link was the odd one
                  out of the three). The modal fetches the live rate JSON so it reads the same
                  fees.ts figures as the PDF; the PDF itself is still one click away inside the
                  modal for anyone who wants the formal document. */}
              <button type="button" className="cq-info-btn" onClick={() => setModal("fees")}>
                Schedule of Fees
              </button>
            </div>
            {/* Session APD: where an intermediary's terms are, in place of the button they are not offered. */}
            {standardTermsApplyTo(quote) ? null : <p className="cq-terms-note">{AGREEMENT_TERMS_NOTE}</p>}

            <p className="fineprint">
              By accepting, we will prepare your service agreement and send it to you for signature. Nothing is charged
              yet.
            </p>
          </div>

          {/* Session AAV (Loop 3): the FAQ accordion and the trust strip round the page off. */}
          <FaqSection itemsMode={itemsMode} equipmentOnly={equipmentOnly} standardTerms={standardTermsApplyTo(quote)} />
          <TrustStrip equipmentOnly={equipmentOnly} itemsMode={itemsMode} />
          {/* Session AGY: on a phone the confirm section can sit several screens down, so a
              compact fixed bar keeps the one action reachable. It renders only where
              IntersectionObserver exists (every real browser; never jsdom, so no test surface)
              and yields whenever the real confirm section is on screen. Never prints. */}
          <MobileAcceptBar
            label={equipmentOnly ? "Your quote" : selected ? `${TIER_LABEL[selected]} plan` : "Choose a plan"}
            price={selected ? `${gbp(quote.tiers[selected].annual)} + VAT / year` : ""}
          />
        </>
      )}

      {/* Session AHS (Item 2): Share/Print sit underneath the maintenance schedule / terms
          section (the confirm box housing the Terms and Conditions / Maintenance Schedule
          buttons) and render unconditionally -- on page load, mid-review or already
          confirmed -- reverting Session AHR's post-confirmation-only placement. One render
          site (never a second copy in AcceptedPanel), gated only on the quote not being
          expired: an expired link has nothing useful to share or print, the one state the
          Director's three named states (accepted / mid-review / confirmed) do not cover. */}
      {quote.status !== "expired" && <ShareprintSection quoteRef={quote.quoteRef} />}

      <Footer />

      {modal === "terms" && standardTermsApplyTo(quote) && <TermsModal onClose={() => setModal(null)} equipmentOnly={equipmentOnly} itemsMode={itemsMode} />}
      {modal === "maintenance" && <MaintenanceModal onClose={() => setModal(null)} itemsMode={itemsMode} equipmentOnly={equipmentOnly} />}
      {modal === "fees" && <ScheduleOfFeesModal onClose={() => setModal(null)} />}
    </Shell>
  );
}

// Session AGY: the mobile quick-accept bar. Fixed to the bottom of the viewport below 720px
// (CSS hides it on desktop and in print via .cq-noprint), naming the selected plan and price
// with one action that jumps to the real confirm section. Feature-gated on
// IntersectionObserver: jsdom has none, so the bar never renders under tests and can never
// duplicate a pinned price/label string there; every real browser has it. It hides itself
// whenever #cq-confirm is on screen, so it never sits on top of the true accept button.
function MobileAcceptBar({ label, price }: { label: string; price: string }) {
  // Environment gate ONLY: jsdom has no IntersectionObserver, every real browser does, so the
  // bar never renders under tests (and can never duplicate a pinned price/label string there).
  // The visibility itself is driven by a passive scroll listener + rect maths rather than the
  // observer, which some embedded/headless compositors never tick.
  const [supported] = useState(() => typeof IntersectionObserver !== "undefined");
  const [confirmOnScreen, setConfirmOnScreen] = useState(false);
  useEffect(() => {
    if (!supported) return;
    const check = () => {
      const t = document.getElementById("cq-confirm");
      if (!t) return;
      const r = t.getBoundingClientRect();
      setConfirmOnScreen(r.top < window.innerHeight && r.bottom > 0);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [supported]);
  if (!supported || confirmOnScreen) return null;
  const jump = () => {
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.getElementById("cq-confirm")?.scrollIntoView?.({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };
  return (
    <div className="cq-accept-bar cq-noprint">
      <div className="cq-accept-bar-info">
        <span className="cq-accept-bar-label">{label}</span>
        {price && <span className="cq-accept-bar-price">{price}</span>}
      </div>
      <button type="button" onClick={jump}>
        Review and accept
      </button>
    </div>
  );
}

// The page shell: the dark header band (logo + contact) above the centred content column on
// the LIGHT canvas (Session AHD, Director decision, reverting Session AGZ). The band is a
// deliberate contrast block, which is the whole point of it, and it prints dark too because
// the white logo needs a dark ground. Still the page's OWN scoped `.cq` treatment, never the
// app shell's bg-ele-bg classes.
// Session APL (D60): `caption` is the line under the logo. Every page keeps "Elevator Service Agreement"
// except an equipment-only quote, which passes EQUIPMENT_ONLY_CAPTION. The loading and error shells cannot
// know the shape yet, so they keep the default.
function Shell({ children, caption = "Elevator Service Agreement" }: { children: ReactNode; caption?: string }) {
  return (
    <div className="cq">
      <div className="cust-hdr">
        <div className="brand">
          <img className="brand-logo" src="/brand/ele-logo.png" alt="East London Elevators" />
          <small>{caption}</small>
        </div>
        <div className="hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
          <br />
          <a href="https://eastlondonelevators.co.uk" target="_blank" rel="noopener noreferrer">
            eastlondonelevators.co.uk
          </a>
        </div>
      </div>
      <div className="wrap ele-fade-in">{children}</div>
    </div>
  );
}

function TierCard({
  tierKey,
  tier,
  recommended,
  selected,
  onSelect,
  surcharges,
  hasEquipment = false,
  careHomeTotal = 0,
  planMode = false,
  itemsMode = false,
}: {
  tierKey: PublicTierKey;
  tier: PublicQuote["tiers"][PublicTierKey];
  recommended: boolean;
  selected: boolean;
  onSelect: () => void;
  // The named postcode-surcharge lines: the same on every tier (each card's price
  // already includes them), shown per card so no column reads as surcharge-free.
  surcharges: PublicQuote["surcharges"];
  // ACP mixed quotes: the card headline shows the LIFT portion only (the equipment is
  // its own always-included block, the same on every plan), with the site total noted.
  hasEquipment?: boolean;
  careHomeTotal?: number;
  // Session ACS (a quote with tier-covered additional items): the headline is the
  // plan's maintenance figure -- lift maintenance plus ITS covered items, so the plans
  // genuinely differ by the coverage premium -- and the flat extras (equipment +
  // maintenance-only items) are separate always-included blocks, noted on the all-in
  // line. False for every legacy quote, whose card is exactly the pre-ACS treatment.
  planMode?: boolean;
  // Session ACV (a LIFT-LESS covered quote): the card copy generalises -- parts cover
  // follows the covered items, nothing reads "per lift", and Complete's per-lift
  // out-of-hours inclusion is omitted (Session ADA, Director decision 12 July 2026:
  // gates-only Complete has no free included call-outs). False everywhere else, so every
  // existing card is byte-identical.
  itemsMode?: boolean;
}) {
  // The card root is a DIV (not a label): the CTA button must not nest inside a label
  // wrapping a different control (invalid HTML; ambiguous for assistive tech). Clicking
  // anywhere on the card still selects (the root onClick); the small label wraps just the
  // radio + name, and the radio keeps its concise accessible name.
  return (
    <div
      className={`ctier ${tierKey}${recommended ? " rec" : ""}${selected ? " sel" : ""}`}
      data-key={tierKey}
      onClick={onSelect}
    >
      {recommended && <span className="rec-badge">RECOMMENDED</span>}
      <label className="ctier-head">
        <input
          type="radio"
          name="tier"
          value={tierKey}
          checked={selected}
          onChange={onSelect}
          aria-label={TIER_LABEL[tierKey]}
        />
        <span className="ctier-name">{TIER_LABEL[tierKey]}</span>
      </label>
      {(() => {
        // planMode: the headline is the plan's maintenance figure (covered items inside);
        // the all-in line notes the separated flat extras only when some exist (a
        // covered-only quote has none, so its headline IS the all-in figure).
        const planSeparated = planMode && tier.annual !== tier.maintenanceAnnual;
        const headline = planMode ? tier.maintenanceAnnual : hasEquipment ? tier.liftAnnual ?? tier.annual - careHomeTotal : tier.annual;
        const suffix = planSeparated ? " (plan)" : !planMode && hasEquipment ? " (lifts)" : "";
        return (
          <>
            <div className="ctier-price">
              {gbp(headline)} <span className="u">ex VAT / year{suffix}</span>
            </div>
            {planSeparated ? (
              <div className="ctier-mo">
                {gbp(tier.annual)} <span className="u">/ year with your included extras</span>
              </div>
            ) : !planMode && hasEquipment ? (
              <div className="ctier-mo">
                {gbp(tier.annual)} <span className="u">/ year with your care equipment</span>
              </div>
            ) : (
              <div className="ctier-mo">
                {gbp(tier.monthly)} <span className="u">/ month equivalent</span>
              </div>
            )}
          </>
        );
      })()}
      {tier.promoMonthly !== null && <div className="ctier-promo">{gbp(tier.promoMonthly)}/month during your offer</div>}
      <p className="ctier-desc">{(itemsMode ? TIER_CARD_COPY_ITEMS : TIER_CARD_COPY)[tierKey].blurb}</p>
      {/* What is included: a tick per line, so the three cards compare at a glance
          (Session AAV, the pricing-card treatment from the design refs). */}
      <ul className="ctier-incl">
        {(itemsMode ? TIER_CARD_COPY_ITEMS : TIER_CARD_COPY)[tierKey].includes.map((line) => (
          <li key={line}>
            <span className="tick" aria-hidden>
              {"✓"}
            </span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {surcharges.length > 0 && (
        <div className="ctier-sur">
          {surcharges.map((s) => (
            <div key={s.key}>
              {s.label}: {gbp2(s.perVisit)}/visit ({gbp(s.annual)}/yr), included above
            </div>
          ))}
        </div>
      )}
      <div className="ctier-rates">
        <div className="r">
          <span>First hour (in hours)</span>
          <span>{gbp2(tier.call1)}</span>
        </div>
        <div className="r">
          <span>Additional hour</span>
          <span>{gbp2(tier.callAdd)}</span>
        </div>
        <div className="r">
          <span>First hour (out of hours)</span>
          <span>{gbp2(tier.callOOH)}</span>
        </div>
        {/* The 15-minute labour rule (Director, 5 October 2026), the same sentence the quote PDF's tier card prints. */}
        <p className="ctier-step">{LABOUR_INCREMENT_SENTENCE}</p>
      </div>
      {/* One clear call to action per card: selecting scrolls to the confirm step. The
          selected card reads action-forward ("Continue with...") so the next step is always
          visible; smooth scrolling respects prefers-reduced-motion. */}
      <button
        type="button"
        className="ctier-cta"
        onClick={(e) => {
          e.stopPropagation(); // the card root also selects; one call is enough
          onSelect();
          const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          document.getElementById("cq-confirm")?.scrollIntoView?.({ behavior: reduce ? "auto" : "smooth", block: "start" });
        }}
      >
        {selected ? `Continue with ${TIER_LABEL[tierKey]}` : `Choose ${TIER_LABEL[tierKey]}`}
      </button>
    </div>
  );
}

// Read-only price breakdown for the selected tier, shown when the quote carries
// additional items, care-home equipment or named postcode surcharges. The tier cards
// above still show the all-in site total; this itemises how that total splits (lift
// maintenance + surcharge pass-throughs + flat extras), so the customer sees exactly
// what they are paying for. Extras are never discounted. The surcharge lines are the
// same on every tier, so the breakdown holds whichever tier is selected.
function PriceBreakdown({
  tierLabel,
  tier,
  additionalItems,
  careHomeItems,
  additionalItemsTotal,
  careHomeTotal,
  surcharges,
  surchargeAnnualTotal,
  coveredItems = [],
  coveredTotal = 0,
  itemsMode = false,
  equipmentOnly = false,
}: {
  tierLabel: string;
  tier: PublicQuote["tiers"][PublicTierKey];
  additionalItems: PublicQuote["additionalItems"];
  careHomeItems: PublicQuote["careHomeItems"];
  additionalItemsTotal: number;
  careHomeTotal: number;
  surcharges: PublicQuote["surcharges"];
  surchargeAnnualTotal: number;
  // Session ACS: the tier-covered additional items AT THE SELECTED TIER's figure (the
  // parent maps the per-tier annuals down to the chosen plan). Empty for every legacy
  // quote, whose breakdown is then exactly the pre-ACS one. Session ACV: frequencyLabel
  // is the item's serviced cadence on a lift-less quote (null on mixed quotes).
  coveredItems?: { description: string; qty: number; annual: number; frequencyLabel?: string | null }[];
  coveredTotal?: number;
  // Session ACV (a LIFT-LESS covered quote): there is no lift maintenance at all -- the
  // covered items ARE the plan -- so the zero "Lift maintenance" row is dropped. False
  // everywhere else, so every existing breakdown is byte-identical.
  itemsMode?: boolean;
  // Session APL (Director decision D60): an EQUIPMENT-ONLY quote has no lift and no tier. The heading
  // names no tier (it used to name whichever hidden tier was posted), the "Lift maintenance" row goes the
  // way the gates-only one did (a non-zero residual, which is how a surcharge the office FOLDED into the
  // price arrives, shows as "Price adjustment"), and the small print drops "every tier" and the multi-lift
  // discount. False everywhere else, so every other breakdown is byte-identical.
  equipmentOnly?: boolean;
}) {
  const subLine = (it: { description: string; price: number; qty: number }) =>
    `${it.description} x ${it.qty} at ${gbp(it.price)}/yr = ${gbp(it.price * it.qty)}`;
  return (
    <div className="sec">
      <div className="ibd-h">{equipmentOnly ? "Your price breakdown" : `Your price breakdown (${tierLabel})`}</div>
      <div className="ibd">
        {/* With surcharge lines itemised below, the maintenance row excludes them so
            the rows sum exactly to the total; with none this is maintenance as before.
            Session ACS: the covered items are itemised as their own rows, so they are
            excluded here too (maintenanceAnnual includes them). Session ACV: on a
            lift-less covered quote there is no lift maintenance -- the zero row is
            dropped -- but a NON-ZERO residual (a manual price adjustment, or fractional
            extras rounding) still renders so the breakdown always sums to the total. */}
        {!itemsMode && !equipmentOnly ? (
          <div className="ibd-row">
            <div className="ibd-nm">Lift maintenance</div>
            <div className="ibd-c">{gbp(tier.maintenanceAnnual - surchargeAnnualTotal - coveredTotal)}</div>
          </div>
        ) : Math.round(tier.maintenanceAnnual - surchargeAnnualTotal - coveredTotal) !== 0 ? (
          <div className="ibd-row">
            <div className="ibd-nm">Price adjustment</div>
            <div className="ibd-c">{signedGbp(tier.maintenanceAnnual - surchargeAnnualTotal - coveredTotal)}</div>
          </div>
        ) : null}

        {/* Session ACS: each tier-covered item at the selected plan's figure. Session ACV:
            a lift-less item also states its serviced cadence. */}
        {coveredItems.map((it, i) => (
          <div className="ibd-row" key={`cov-${i}`}>
            <div className="ibd-nm">
              {it.description}
              {it.qty > 1 ? ` x ${it.qty}` : ""}
              <ul className="ibd-sub">
                <li>Covered by your {tierLabel} plan; its parts and coverage follow the plan's terms</li>
                {it.frequencyLabel ? <li>Serviced {it.frequencyLabel.toLowerCase()}</li> : null}
              </ul>
            </div>
            <div className="ibd-c">{gbp(it.annual)}</div>
          </div>
        ))}

        {surcharges.map((s) => (
          <div className="ibd-row" key={s.key}>
            <div className="ibd-nm">
              {s.label}
              <ul className="ibd-sub">
                <li>{gbp2(s.perVisit)} per planned visit</li>
              </ul>
            </div>
            <div className="ibd-c">{gbp(s.annual)}</div>
          </div>
        ))}

        {additionalItems.length > 0 && (
          <div className="ibd-row">
            <div className="ibd-nm">
              Additional items
              <ul className="ibd-sub">
                {additionalItems.map((it, i) => (
                  <li key={`add-${i}`}>{subLine(it)}</li>
                ))}
              </ul>
            </div>
            <div className="ibd-c">{gbp(additionalItemsTotal)}</div>
          </div>
        )}

        {careHomeItems.length > 0 && (
          <div className="ibd-row">
            <div className="ibd-nm">
              Care-home equipment
              <ul className="ibd-sub">
                {/* ACP: an equipment row's annual is its frequency-priced figure (never the
                    raw catalogue price x qty once a cadence is stepped up). */}
                {careHomeItems.map((it, i) => {
                  const serviced = equipmentServicedWord(it);
                  return (
                    <li key={`care-${i}`}>
                      {it.description} x {it.qty}
                      {serviced ? `, serviced ${serviced}` : ""} = {gbp(equipmentItemAnnual(it))}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="ibd-c">{gbp(careHomeTotal)}</div>
          </div>
        )}

        <div className="ibd-row ibd-total">
          <div className="ibd-nm">Total per year (ex VAT)</div>
          <div className="ibd-c">{gbp(tier.annual)}</div>
        </div>
      </div>
      {surcharges.length > 0 && equipmentOnly && <p className="fineprint">{SURCHARGE_PASS_THROUGH_LINE_SINGLE_PRICE}</p>}
      {surcharges.length > 0 && !equipmentOnly && (
        <p className="fineprint">
          Zone surcharges are statutory and operational cost pass-throughs (congestion charging and controlled
          parking), charged per planned visit. They are not an East London Elevators charge and apply equally to
          every tier.
        </p>
      )}
      {!equipmentOnly && (additionalItems.length > 0 || careHomeItems.length > 0) && (
        <p className="fineprint">
          Additional items and care-home equipment are charged at a flat rate and are not part of any multi-lift
          discount.
        </p>
      )}
      {coveredItems.length > 0 && (
        <p className="fineprint">
          Plan-covered items are included in each plan's price at that plan's rate, and their parts and coverage
          follow the plan you choose. They are never part of any multi-lift discount.
        </p>
      )}
    </div>
  );
}

// AI-written narrative (no prices), shown below the tier options. The text is already
// house-style clean (sanitised server-side); we render it as paragraphs.
function QuoteNarrative({ text }: { text: string }) {
  const paras = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paras.length === 0) return null;
  return (
    <section className="sec">
      <div className="sec-title">About your service</div>
      <div className="narrative">
        {paras.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </section>
  );
}

// Session AAU Loop 4: the plain-English "explain this quote" for the customer. Always present (AI text
// or the static fallback), rendered under its own heading below the tiers.
function QuoteExplanation({ text }: { text: string }) {
  const paras = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paras.length === 0) return null;
  return (
    <section className="sec">
      <div className="sec-title">Understanding your quote</div>
      <div className="narrative">
        {paras.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </section>
  );
}

// Session APD: the FAQ's pointer to the full detail. For a customer who signs the Standard ESA it is the
// Terms and Conditions button; an intermediary is not offered that button (standardTermsApplyTo), so for
// them it points at the agreement itself and never at a button that is not on their page. Named once and
// used by every FAQ variant below, so FaqSection swaps the one sentence and nothing else.
//
// Session APJ: the pointer is built from its two halves, because only a page WITH tier cards has a
// comparison table to point at. The lift and items-mode answers keep the full sentence, byte for byte; the
// equipment-only answer ends on the detail pointer alone. FAQ_AGREEMENT_POINTER is the one swap for both
// forms (a Map, so a paragraph can never match an inherited object key).
const FAQ_COMPARISON_TABLE = "The comparison table above shows the three plans side by side.";
const FAQ_DETAIL_TERMS = "The full detail is in the Terms and Conditions.";
const FAQ_DETAIL_AGREEMENT = "The full detail is in the service agreement we send you to sign.";
const FAQ_FULL_DETAIL_TERMS = `${FAQ_COMPARISON_TABLE} ${FAQ_DETAIL_TERMS}`;
const FAQ_FULL_DETAIL_AGREEMENT = `${FAQ_COMPARISON_TABLE} ${FAQ_DETAIL_AGREEMENT}`;
const FAQ_AGREEMENT_POINTER = new Map<string, string>([
  [FAQ_FULL_DETAIL_TERMS, FAQ_FULL_DETAIL_AGREEMENT],
  [FAQ_DETAIL_TERMS, FAQ_DETAIL_AGREEMENT],
]);

// Session APJ: two paragraphs the equipment-only variant shares word for word with the default FAQ, named
// once so the two cannot drift apart.
const FAQ_SURCHARGE_WHERE =
  "If your site is inside the Congestion Charge Zone, or in Westminster or Kensington and Chelsea controlled parking areas, a small charge per planned visit covers those statutory and operational costs.";
const FAQ_NOTHING_CHARGED = `Nothing is charged until your agreement is signed. Invoices are issued 30 days before each scheduled service. Any questions, call us on ${PHONE}.`;

// Frequently asked questions (Session AAV, Loop 3): a native details/summary accordion --
// keyboard and screen-reader accessible for free. Plain English, zero lift knowledge assumed.
// A beforeprint listener opens every item so a printed/saved copy carries the answers.
const FAQ_ITEMS: { q: string; a: string[] }[] = [
  {
    q: "What does each level of cover include?",
    // Session AKB follow-up: Care and Complete carry the same real-figure disclosure as the tier
    // cards and the comparison table (£138.55 / £122.25 against Core's £163 anchor,
    // CALLOUT_FIRST_HOUR in server/src/modules/billing/billing.ts), and Complete's included
    // call-outs are "a year", not "per lift" -- allowances.ts's inclusiveCallOutsForTier is a flat
    // six-per-contract-year allowance, never multiplied by lift count.
    a: [
      "Core covers your scheduled maintenance visits, safety compliance and the labour for routine adjustments; call-outs and parts are charged at our Schedule of Fees rates when needed.",
      "Care adds parts cover of up to £250 per lift per year and 15% off call-out labour, £138.55 instead of £163 for the first hour.",
      "Complete adds six included out-of-hours call-out visits a year, parts cover of up to £400 per lift per year, and 25% off any further call-outs, £122.25 instead of £163 for the first hour.",
      // Session AMP: the FAQ is where a customer goes to check what a plan actually costs them, so
      // the one chargeable rate the tier does NOT change is answered in the same breath as the ones
      // it does. The trailing "so routine repairs rarely bring a separate invoice" was removed from
      // the Care line above for the same reason it was removed from the tier card.
      REPAIR_LABOUR_FAQ,
      FAQ_FULL_DETAIL_TERMS,
    ],
  },
  {
    q: "How is the price worked out?",
    a: [
      "The price reflects your equipment and how often it is serviced: the number of lifts, each lift's type, size and age, and the visit frequency you choose.",
      // Session AHC. The band CAN count lifts across a customer's other buildings, but only where
      // the office has grouped those quotes; an ungrouped quote is priced on its own lifts. The
      // first wording promised the wider count unconditionally, which is untrue for exactly the
      // customer this feature exists for -- one holding two separate, not-yet-grouped quotes -- who
      // would read it, assume four lifts had been counted, and find both priced at 0%. So it says
      // what always holds and invites the question rather than asserting an outcome.
      "Where we maintain several lifts for you, a multi-lift discount is applied automatically. If you have lifts at more than one building, ask us to price them together. All prices exclude VAT.",
    ],
  },
  {
    q: "What is LOLER, and who is responsible?",
    a: [
      "LOLER 1998 is the law that requires passenger lifts to have a periodic thorough examination by a competent person, usually arranged through your building's insurer. It is separate from maintenance, in the same way a vehicle's MOT is separate from its servicing.",
      "The building owner or manager is responsible for arranging those examinations. Our maintenance keeps your lift safe and reliable between them: we track your examination dates, remind you when one is due, and quote for any remedial work the examiner raises (covered by your plan where parts cover applies).",
    ],
  },
  {
    q: "What are the zone surcharges?",
    a: [
      FAQ_SURCHARGE_WHERE,
      // Session APL (Director decision D60): the last sentence used to read "If no surcharge line appears on
      // this quote, none applies to your site", and "they are itemised on your quote" before it. Both were
      // false whenever the office FOLDED a surcharge into the price (surchargePresentation "fold": it is
      // charged, with no line). This wording is true whether the office shows, folds or waives it. Shared by
      // the lift and the gates-only FAQ (no items-mode override); the equipment-only answer has its own.
      "They are cost pass-throughs, not an East London Elevators charge, are the same on every tier, and only apply where your postcode is inside a zone. Where one applies, it is either shown as its own line on this quote or already included in the price.",
    ],
  },
  {
    q: "How do I accept, and what happens next?",
    a: [
      "Choose your plan, add a preferred start date if you have one, and press Accept. We then prepare your service agreement and email you a secure link to sign it electronically.",
      FAQ_NOTHING_CHARGED,
    ],
  },
];

// Session ACV: the two FAQ answers with lift-specific facts get an items-mode variant on a
// lift-less covered quote (parts cover follows the covered items; Complete's per-lift
// out-of-hours inclusion is omitted -- Session ADA, Director decision 12 July 2026:
// gates-only Complete has no free included call-outs; the price basis names the items, not
// lifts). Every other shape renders the original items verbatim.
const FAQ_ITEMS_MODE_OVERRIDES: Record<string, string[]> = {
  // Session AKB follow-up: same £ disclosure as the lift-based variant above. This variant never
  // said "per lift" for call-outs (gates-only Complete has no included call-outs at all, Session
  // ADA), so there is no per-lift wording to correct here, only the figure to add.
  "What does each level of cover include?": [
    "Core covers your scheduled maintenance visits, safety compliance and the labour for routine adjustments; call-outs and parts are charged at our Schedule of Fees rates when needed.",
    "Care adds parts cover of up to £250 per covered item per year and 15% off call-out labour, £138.55 instead of £163 for the first hour.",
    "Complete adds parts cover of up to £400 per covered item per year and 25% off any further call-outs, £122.25 instead of £163 for the first hour.",
    // Session AMP, same addition as the lift-based variant above.
    REPAIR_LABOUR_FAQ,
    FAQ_FULL_DETAIL_TERMS,
  ],
  "How is the price worked out?": [
    "The price reflects the items covered by your plan and the service level you choose: each item's own price forms the Core plan, and the Care and Complete plans add their cover on top.",
    "All prices exclude VAT.",
  ],
  "What is LOLER, and who is responsible?": [
    "LOLER 1998 is the law that requires lifting equipment, such as passenger lifts and hoists, to have periodic thorough examinations. The items covered by this quotation (gates, doors and barriers) are not lifting equipment, so no LOLER examination applies to them: we maintain them to the manufacturer's recommendations and PUWER 1998.",
    "If your site also has lifts or lifting equipment looked after elsewhere, arranging their examinations remains the duty holder's responsibility, and we are happy to advise.",
  ],
};

// Session APJ: the EQUIPMENT-ONLY shape (no lift and no plan-covered item, ACP spec 2.4) has no tier step at
// all: one price block, each item with the servicing frequency it is priced for, and an "Accept quote" button.
// Every default answer described a page it is not (three plans, parts cover "per lift", the comparison table,
// lifts' type, size and age, "Choose your plan"), so EVERY entry has an equipment variant, and the first has
// its own question because "each level of cover" is itself the false premise. The equipment IS serviced, on
// its own frequency; this only corrects what the FAQ says about the page.
//
// What each answer rests on, all checked in Session APJ (the second group after the session's adversarial
// review showed the first draft overclaimed):
//   - The shape is "no lift and no COVERED item", so it can also carry FLAT (maintenance-only) additional
//     items, a roller shutter say, priced in the same one price and shown in the price breakdown. So the
//     answers speak of "every item listed above", and keep the servicing frequency to the care equipment,
//     which is what shows one.
//   - The ESA for this shape names the "Care equipment maintenance" service with no tier and no parts
//     allowance (esaContract.ts, tierPartsNote) and charges call-outs under its Conditions clause 3. The
//     Schedule of Fees prices repairs and replacement parts as "Quoted", with all additional work agreed in
//     writing first (scheduleOfFees.ts), so the answer says exactly that rather than "charged at our rates".
//   - Only PEOPLE-LIFTING units get an examination clock (pipeline/equipment.ts: peopleLifting is a minimum of
//     two visits a year; a fixed-height nursing chair has none), and the LOLER reminder job needs a due date
//     (lolerReminderPipeline.ts). So the tracking promise is made only "for each item that needs these
//     examinations". The LOLER sentence mirrors the equipment reminder's own (lolerReminders.ts).
//   - A zone surcharge DOES apply to a lift-less quote (quotes/pricing.ts), but the office chooses per quote
//     whether it is itemised, folded into the price or waived (surchargePresentation, publicQuote.ts), and the
//     equipment-only quote PDF never itemised it. So the answer says a surcharge that applies is already in
//     the price, and never "if no surcharge line appears, none applies" (the lift answer's sentence, which a
//     folded surcharge made false; Session APL corrected that one too, D60).
//
// Keyed by the default question like FAQ_ITEMS_MODE_OVERRIDES, but FaqSection DROPS a default entry that has
// no equipment variant rather than showing it: a question added to FAQ_ITEMS later must never reach this page
// in its lift wording, which is the defect this map exists to remove.
const FAQ_ITEMS_EQUIPMENT_OVERRIDES: Record<string, { q: string; a: string[] }> = {
  "What does each level of cover include?": {
    q: "What does the price include?",
    a: [
      "The price covers the scheduled servicing of every item listed above, and each item of care equipment is serviced at the frequency shown against it. It is one price, with no plans to choose between.",
      "It covers maintenance only. Call-outs are charged at our Schedule of Fees rates when needed, and any repair or replacement part is quoted and agreed with you in writing before the work starts.",
      FAQ_DETAIL_TERMS,
    ],
  },
  "How is the price worked out?": {
    q: "How is the price worked out?",
    a: [
      "Each item listed above has its own price, and each item of care equipment is priced for how often it is serviced, at the frequency shown against it.",
      "All prices exclude VAT.",
    ],
  },
  "What is LOLER, and who is responsible?": {
    q: "What is LOLER, and who is responsible?",
    a: [
      "LOLER 1998 is the law that requires equipment used to lift or lower people, such as hoists, to have a thorough examination by a competent person at least every six months, usually arranged through your insurer. It is separate from maintenance, in the same way a vehicle's MOT is separate from its servicing.",
      "The equipment's owner or operator is responsible for arranging those examinations. Our maintenance keeps your equipment safe and reliable between them, and for each item that needs these examinations we track its examination dates, remind you when one is due, and quote for any remedial work the examiner raises.",
    ],
  },
  "What are the zone surcharges?": {
    q: "What are the zone surcharges?",
    a: [
      FAQ_SURCHARGE_WHERE,
      "They are cost pass-throughs, not an East London Elevators charge, and only apply where your postcode is inside a zone. Where one applies, it is already included in the price on this quote.",
    ],
  },
  "How do I accept, and what happens next?": {
    q: "How do I accept, and what happens next?",
    a: [
      "Add a preferred start date if you have one, and press Accept quote. We then prepare your service agreement and email you a secure link to sign it electronically.",
      FAQ_NOTHING_CHARGED,
    ],
  },
};

// `standardTerms` is REQUIRED rather than defaulted: the one call site must say whether this customer is
// offered the standard terms (standardTermsApplyTo), so a new call site cannot silently point an
// intermediary at a Terms and Conditions button their page does not have (Session APD). `equipmentOnly` is
// REQUIRED for the same reason (Session APJ): a call site that forgets it would show an equipment-only page
// the lift FAQ, which is the defect it was added to fix.
function FaqSection({
  itemsMode = false,
  equipmentOnly,
  standardTerms,
}: {
  itemsMode?: boolean;
  equipmentOnly: boolean;
  standardTerms: boolean;
}) {
  const shaped = equipmentOnly
    ? FAQ_ITEMS.flatMap((item) => FAQ_ITEMS_EQUIPMENT_OVERRIDES[item.q] ?? [])
    : itemsMode
      ? FAQ_ITEMS.map((item) => (FAQ_ITEMS_MODE_OVERRIDES[item.q] ? { q: item.q, a: FAQ_ITEMS_MODE_OVERRIDES[item.q]! } : item))
      : FAQ_ITEMS;
  const items = standardTerms
    ? shaped
    : shaped.map((item) => ({ q: item.q, a: item.a.map((p) => FAQ_AGREEMENT_POINTER.get(p) ?? p) }));
  const ref = useRef<HTMLDivElement>(null);
  // Print support: open every item before printing so the saved copy carries the answers,
  // then restore the reader's open/closed state afterwards.
  useEffect(() => {
    const details = () => Array.from(ref.current?.querySelectorAll("details") ?? []);
    let wasOpen: boolean[] = [];
    const before = () => {
      wasOpen = details().map((d) => d.open);
      details().forEach((d) => {
        d.open = true;
      });
    };
    const after = () => {
      details().forEach((d, i) => {
        d.open = wasOpen[i] ?? false;
      });
    };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);
  return (
    <div className="sec cq-faq" ref={ref}>
      {/* A real heading so the new section is reachable by heading navigation. */}
      <h2 className="sec-title">Common questions</h2>
      {items.map((item) => (
        <details key={item.q}>
          {/* The summary keeps its DEFAULT display (WebKit drops the disclosure role and
              expanded state from a display:flex summary); the flex layout + marker live on
              the inner span instead. */}
          <summary>
            <span className="cq-faq-q">{item.q}</span>
          </summary>
          <div className="cq-faq-a">
            {item.a.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

// Trust and compliance strip (Session AAV, Loop 3): the quiet, factual reassurance row.
// Registered company facts, the standards worked to, and the no-charge-yet promise.
// Session APL (D60): an equipment-only page never claims BS EN 81, the lift standard.
function TrustStrip({ equipmentOnly = false, itemsMode = false }: { equipmentOnly?: boolean; itemsMode?: boolean }) {
  return (
    <div className="sec cq-trust">
      <h2 className="sr-only">About East London Elevators</h2>
      <div className="cq-trust-grid">
        <div>
          <div className="cq-trust-k">East London Elevators Ltd</div>
          <div className="cq-trust-v">Company 15429089 · VAT GB493339461</div>
        </div>
        <div>
          <div className="cq-trust-k">Works to standard</div>
          <div className="cq-trust-v">{equipmentOnly ? EQUIPMENT_STANDARDS : itemsMode ? COVERED_ITEMS_STANDARDS : "LOLER 1998, PUWER 1998 and BS EN 81"}</div>
        </div>
        <div>
          <div className="cq-trust-k">Signed securely online</div>
          <div className="cq-trust-v">Your agreement arrives by email for electronic signature</div>
        </div>
        <div>
          <div className="cq-trust-k">Nothing charged today</div>
          <div className="cq-trust-v">No payment is taken until your agreement is signed</div>
        </div>
      </div>
    </div>
  );
}

// Session AHS (Item 2): Share/Print moved OUT of this panel and into their own always-
// rendered section below the confirm box (see ShareprintSection below), reverting Session
// AHR's post-confirmation-only placement. This panel is now just the acceptance notice.
function AcceptedPanel({
  tier,
  message,
  equipmentOnly = false,
}: {
  tier: PublicTierKey;
  message: string;
  equipmentOnly?: boolean;
}) {
  return (
    <div className="sec notice">
      <div className="ov-tick" aria-hidden="true">
        {"✓"}
      </div>
      {/* An equipment-only quote has no tier to name (there was no plan choice). */}
      <h2>Thank you. {equipmentOnly ? "Quote" : TIER_LABEL[tier]} accepted</h2>
      <p>
        {message ||
          `Your service agreement is being prepared and will be sent to you for signature shortly. If you have any questions, call the office on ${PHONE}.`}
      </p>
    </div>
  );
}

// Session AHS (Item 2): Share and Print, in their own section underneath the maintenance
// schedule / terms and conditions section (the confirm box that houses the Terms and
// Conditions / Maintenance Schedule buttons). Rendered unconditionally by the parent --
// on page load, mid-review or already accepted -- so there is exactly one render site
// rather than a second copy duplicated into the post-confirmation panel. Styled as a plain
// .sec card, the same white-card treatment every other section on this page uses, rather
// than the AcceptedPanel-specific centred-notice styling it used to inherit for free.
// Exported (Session AJN) for the same reason Footer already is: the renewal accept page reuses
// this section directly rather than a second implementation that can drift.
// documentLabel (Session AJV) forwards to ShareButton's own prop of the same name; defaults to
// "Maintenance Quote" so this page's own render sites are unaffected without being touched.
export function ShareprintSection({ quoteRef, documentLabel = "Maintenance Quote" }: { quoteRef: string; documentLabel?: string }) {
  return (
    <div className="sec cq-shareprint cq-noprint">
      <ShareButton quoteRef={quoteRef} documentLabel={documentLabel} />
      <button type="button" onClick={() => window.print()} className="cq-print-btn">
        Print or save a copy
      </button>
    </div>
  );
}

// A white card of three ELE differentiators, between the site strip and the tier section, matching
// the other white cards on the page (Session AA, Item 5: was a jarring dark band). The headings are
// ALL CAPS labels in their brand colour (green / purple / blue); the body copy is dark on white.
function UspBanner({ itemsMode = false, equipmentOnly = false }: { itemsMode?: boolean; equipmentOnly?: boolean }) {
  return (
    <div className="sec usp-band">
      <div className="usp-grid">
        <div className="usp-col">
          <div className="usp-h usp-h-green">RFID SITE LOG</div>
          {/* Session ACV: a lift-less covered quote has no lift car to tap; the covered
              items become registered, taggable equipment, so the copy generalises. */}
          {/* Session APL (D60): an equipment-only quote's RFID tags are on the equipment, whose page shows
              its service history and its examination status. */}
          <div className="usp-b">
            {equipmentOnly
              ? EQUIPMENT_RFID_LINE
              : itemsMode
                ? "Tap your phone on the RFID tag on your equipment to see its service history."
                : "Tap your phone on the inside of the lift car to see your service history and LOLER completion dates."}
          </div>
        </div>
        <div className="usp-col">
          <div className="usp-h usp-h-purple">DIGITAL SERVICE RECORDS</div>
          <div className="usp-b">Every visit logged and always accessible online.</div>
        </div>
        <div className="usp-col">
          <div className="usp-h usp-h-blue">FULLY COMPLIANT WORKS</div>
          <div className="usp-b">
            {equipmentOnly ? `All works carried out to ${EQUIPMENT_STANDARDS}.` : itemsMode ? `All works carried out to ${COVERED_ITEMS_STANDARDS}.` : "All works carried out to LOLER 1998, PUWER 1998 and BS EN 81."}
          </div>
        </div>
      </div>
    </div>
  );
}

// Item 4D: a light-theme modal shell matching the page (dark header band + logo, a scrollable
// body, a close button; closes on the X, an outside click or Escape). Manages focus (moves it
// into the dialog on open, restores it to the trigger on close) and locks page scroll behind
// the overlay, for a keyboard / screen-reader friendly customer-facing modal.
function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Focus management: remember what was focused, move focus into the dialog, lock body scroll.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="cq-modal-overlay" onClick={onClose}>
      <div ref={dialogRef} className="cq-modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <div className="cq-modal-hdr">
          <img className="brand-logo" src="/brand/ele-logo.png" alt="East London Elevators" />
          <button type="button" className="cq-modal-x" aria-label="Close" onClick={onClose}>
            {"✕"}
          </button>
        </div>
        <div className="cq-modal-body">
          <h2 className="cq-modal-title">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

// WHETHER THE STANDARD TERMS AND CONDITIONS BUTTON SHOWS ON THIS PAGE.
//
// Session APD (21 September 2026) withheld the button, the modal and pointed an intermediary at
// AGREEMENT_TERMS_NOTE instead, keyed on the server's standardTermsApply flag (client type). That was D52
// on the decisions board, left open for the Director to rule on. DIRECTOR OVERRIDE (29 September 2026): the
// button stays for every quote, whoever it went to. The server's standardTermsApply flag is no longer
// consulted for this decision; the parameter is kept only so every existing call site (and
// standardTermsGate.test.ts's scan for a call to this function) still compiles unchanged.
//
// EVERY render of the Terms and Conditions button or TermsModal still asks this, here and on
// RenewalAccept.tsx: standardTermsGate.test.ts scans src/ and fails on one that does not.
export function standardTermsApplyTo(_view: { standardTermsApply?: boolean }): boolean {
  return true;
}

// Session APD: what a customer who is not offered the standard terms is told in their place. True on both
// pages, which each go on to send the customer a service agreement to sign, and the Intermediary ESA carries
// its full Conditions inside that agreement. Exported for RenewalAccept.tsx, the same reuse-not-fork reason
// as TermsModal below.
export const AGREEMENT_TERMS_NOTE = "Your terms and conditions are set out in full in the service agreement we send you to sign.";

// Exported (Session AJN), same reason as ShareprintSection above: the renewal accept page opens
// this exact modal rather than a forked copy of the T&Cs. Render it only where standardTermsApplyTo
// (above) says the standard terms belong (Session APD).
// Session APL (Director decision D60): `equipmentOnly` shows the same clauses in the signed agreement's own
// equipment-only wording (TERMS_CLAUSES_CARE_EQUIPMENT). Absent (the renewal page, which is lift-only by
// construction) it is the lift set, exactly as before.
// Session AQC (Director decision D72): `itemsMode` (GATES-ONLY: no lift, a tier-covered gate, door,
// barrier or shutter) shows the same clauses in ITS agreement's wording, which has named the plant
// "equipment" since Session ACV while this modal still said "lift". A gates-only quote is tier-priced,
// so only the three clauses that name the plant change; clauses 3 and 6 keep their tier wording.
export function TermsModal({
  onClose,
  equipmentOnly = false,
  itemsMode = false,
}: {
  onClose: () => void;
  equipmentOnly?: boolean;
  itemsMode?: boolean;
}) {
  const clauses = equipmentOnly ? TERMS_CLAUSES_CARE_EQUIPMENT : itemsMode ? TERMS_CLAUSES_COVERED_ITEMS : TERMS_CLAUSES;
  return (
    <Modal title="Terms and Conditions of Contract" onClose={onClose}>
      {clauses.map((c) => (
        <div className="cq-clause" key={c.n}>
          <h3 className="cq-clause-h">
            {c.n}. {c.title}
          </h3>
          {c.body.map((block, i) =>
            block.kind === "p" ? (
              <p key={i}>{block.text}</p>
            ) : (
              <ul key={i}>
                {block.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            ),
          )}
        </div>
      ))}
    </Modal>
  );
}

// Exported (Session AJN), same reason as ShareprintSection above: the renewal accept page opens
// this exact modal rather than a forked copy of the maintenance schedule. A renewal's register is
// lift-only by construction (buildRenewalContext refuses `mixed_contract` at load, so a covered
// gate/door item can never reach this page), so it always calls this with itemsMode left at its
// default false -- there is no renewal-page equivalent of AcceptQuote's lift-less itemsMode shape.
// Session APL (Director decision D60): `equipmentOnly` shows the CARE EQUIPMENT schedule
// (MAINTENANCE_SCHEDULE_CARE_EQUIPMENT, from the ELE-MS-002 checklist) in place of the lift one. It has no
// periodic list, so that heading is left out rather than shown empty.
export function MaintenanceModal({
  onClose,
  itemsMode = false,
  equipmentOnly = false,
}: {
  onClose: () => void;
  itemsMode?: boolean;
  equipmentOnly?: boolean;
}) {
  // Session AQC (D72): the GATES-ONLY schedule is the lift one (as its agreement renders it) with the
  // standards sentence that actually governs a gate: not BS EN 81, the lift standard, nor LOLER.
  const s = equipmentOnly ? MAINTENANCE_SCHEDULE_CARE_EQUIPMENT : itemsMode ? MAINTENANCE_SCHEDULE_COVERED_ITEMS : MAINTENANCE_SCHEDULE;
  // Session ACV: on a lift-less covered quote the shared schedule's call-out line must not
  // name Complete's per-lift OOH inclusion (that benefit does not apply on this shape --
  // Session ADA, Director decision 12 July 2026: gates-only Complete has no free included
  // call-outs); it states the chargeable basis instead -- the same mapping the quote PDF and
  // the ESA apply. Every other shape renders the shared list verbatim.
  const notIncluded = itemsMode
    ? s.notIncluded.map((it) =>
        it.startsWith("Call-outs outside the included allowance")
          ? "Call-outs (chargeable at your tier's call-out rates; Care 15% off, Complete 25% off)"
          : it,
      )
    : s.notIncluded;
  return (
    <Modal title="Maintenance Schedule" onClose={onClose}>
      <h3 className="cq-clause-h">What is included in each visit</h3>
      <ul>{s.included.map((it, i) => <li key={i}>{it}</li>)}</ul>
      {s.periodic.length > 0 && (
        <>
          <h3 className="cq-clause-h">Periodic maintenance (within service visits)</h3>
          <ul>{s.periodic.map((it, i) => <li key={i}>{it}</li>)}</ul>
        </>
      )}
      <h3 className="cq-clause-h">What is not included</h3>
      <ul>{notIncluded.map((it, i) => <li key={i}>{it}</li>)}</ul>
      <p className="cq-modal-footer">{s.footer}</p>
    </Modal>
  );
}

interface ScheduleOfFeesBasis {
  key: string;
  tableLabel: string;
  cardLabel: string;
  cardDetail: string;
  rates: { firstHour: number; addHour: number; travel: number };
}

// Session AKV: one array feeds both the table (desktop) and the stacked cards (narrow viewport)
// below, so the two renderings can never state a different figure, or different basis wording,
// for the same row -- the same "one source, two renderings" discipline this modal already holds
// itself to for the JSON vs the PDF (see the modal's own header comment). tableLabel keeps the
// table's original combined wording byte-for-byte; cardLabel/cardDetail is the label/detail split
// Session AKS proved fits a 390px card (ScheduleOfFeesPage.tsx, now retired -- see below).
function scheduleOfFeesBases(rates: PublicScheduleOfFeesRates): ScheduleOfFeesBasis[] {
  return [
    {
      key: "in",
      tableLabel: "In contract, normal hours",
      cardLabel: "In contract",
      cardDetail: "Normal hours, Monday to Friday, 09:00 to 17:00",
      rates: rates.inContract,
    },
    {
      key: "in-ooh",
      tableLabel: "In contract, weekends, bank holidays and outside normal hours",
      cardLabel: "In contract",
      cardDetail: "Weekends, bank holidays and outside normal hours",
      rates: rates.outOfHours,
    },
    {
      key: "non",
      tableLabel: "Non-contract or expired contract, normal hours",
      cardLabel: "Non-contract",
      cardDetail: "Normal hours, Monday to Friday, 09:00 to 17:00",
      rates: rates.nonContract,
    },
    {
      key: "non-ooh",
      tableLabel: "Non-contract or expired contract, weekends, bank holidays and outside normal hours",
      cardLabel: "Non-contract",
      cardDetail: "Weekends, bank holidays and outside normal hours",
      rates: rates.nonContractOutOfHours,
    },
  ];
}

function ScheduleOfFeesFigure({ label, value }: { label: string; value: number }) {
  return (
    <div className="cq-fees-figure">
      <p className="cq-fees-figure-label">{label}</p>
      <p className="cq-fees-figure-value">{gbp2(value)}</p>
    </div>
  );
}

// Exported (Session AKR), same reuse-not-fork reason as TermsModal/MaintenanceModal above: the
// renewal accept page opens this exact modal too. Unlike those two, the rate card is not static
// client content -- it fetches the live JSON GET /api/public/schedule-of-fees/rates, built from
// the exact same fees.ts functions renderScheduleOfFeesDocument() reads for the PDF (Item 1), so
// this modal can never state a different figure than the document underneath it. The table's
// column order and row order match the PDF's own table exactly (tierRow() in scheduleOfFees.ts);
// only the print-specific sizing is left behind.
//
// Session AKV: below the page's own ~720px mobile breakpoint the table cannot fit the modal's own
// content width without horizontal scroll (the modal narrows with the viewport; the table needs a
// 460px minimum to stay legible), which is why Session AKS built a whole second standalone page
// around this one problem instead of fixing it here. That page is retired (Director decision: "a
// hat on a hat" -- the actual requirement was always that THIS modal read on a phone). Both
// markups below render every time; CSS alone (AcceptQuote.css, reusing this file's own established
// 720px breakpoint) decides which one paints, so there is no viewport listener to keep in sync.
// The card shape is AKS's own, proven to fit a 390px viewport with zero horizontal overflow; only
// the palette changed, to this modal's light-canvas tokens (AKS's page was dark-canvas).
export function ScheduleOfFeesModal({ onClose }: { onClose: () => void }) {
  const [rates, setRates] = useState<PublicScheduleOfFeesRates | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.publicDocuments
      .scheduleOfFeesRates()
      .then((data) => {
        if (!cancelled) setRates(data);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bases = rates ? scheduleOfFeesBases(rates) : null;
  // Session AOX: the technician visit is a FLAT DAY RATE (fees.ts techVisitRates: dayRate,
  // specialArrangementDayRate, capHours). The guard is on the NEW shape, field by field, not on the
  // object existing: an older server's payload carries AOC's hourly keys (inContract, outOfHours...)
  // under the old `technicianVisit` name (the day-rate card is served as `technicianVisitDayRate`, so
  // an old bundle finds no technician object and renders no paragraph); reading it here would print
  // "£NaN". The SPA and the API deploy as separate Render services, so for the minutes between their
  // deploys either side can be a version behind; no paragraph is the correct fallback either way.
  //
  // Session TV2 (Director, 29 October 2026): the card moved to a NEW key, `technicianVisitCharge`, with the fields
  // dayRate, capHours, overageHourRate, overageStepMinutes and furtherDayRate (one day rate, time past the block
  // charged). The guard is on EVERY field: an older server's payload has only the retired `technicianVisitDayRate`
  // (which this bundle never reads, so it can never print the retired "no hourly overage" wording) and then no
  // technician paragraph is rendered at all, never a wrong one.
  const tv = rates?.technicianVisitCharge as Partial<PublicTechVisitRates> | undefined;
  const techVisit =
    tv &&
    typeof tv.dayRate === "number" &&
    typeof tv.capHours === "number" &&
    typeof tv.overageHourRate === "number" &&
    typeof tv.overageStepMinutes === "number" &&
    typeof tv.furtherDayRate === "number"
      ? (tv as PublicTechVisitRates)
      : null;

  return (
    <Modal title="Schedule of Fees" onClose={onClose}>
      <p>
        {/* Session APL (Director decision D60): "the lift or equipment", in step with the PDF's section 1,
            because the one document serves care equipment customers too. */}
        The rate that applies depends on whether the lift or equipment is covered by a current maintenance contract,
        and on the time of the visit. Every rate below is stated in full, ex VAT, per engineer. Normal hours are 09:00 to 17:00,
        Monday to Friday.
      </p>

      {loadFailed && (
        <p role="alert" className="cq-error">
          We could not load the current rates just now. Please try again in a moment, or download the PDF below.
        </p>
      )}
      {!rates && !loadFailed && <p>Loading the current rates...</p>}

      {rates && bases && (
        <>
          <div className="cq-fees-table-wrap">
            <table className="cq-fees-table">
              <thead>
                <tr>
                  <th>Basis</th>
                  <th>First hour</th>
                  <th>Each additional hour</th>
                  <th>Travel, per hour</th>
                </tr>
              </thead>
              <tbody>
                {bases.map((b) => (
                  <tr key={b.key}>
                    <td>{b.tableLabel}</td>
                    <td>{gbp2(b.rates.firstHour)}</td>
                    <td>{gbp2(b.rates.addHour)}</td>
                    <td>{gbp2(b.rates.travel)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cq-fees-cards">
            {bases.map((b) => (
              <div key={b.key} className="cq-fees-card">
                <p className="cq-fees-card-label">{b.cardLabel}</p>
                <p className="cq-fees-card-detail">{b.cardDetail}</p>
                <div className="cq-fees-card-figs">
                  <ScheduleOfFeesFigure label="First hour" value={b.rates.firstHour} />
                  <ScheduleOfFeesFigure label="Each add'l hour" value={b.rates.addHour} />
                  <ScheduleOfFeesFigure label="Travel, per hour" value={b.rates.travel} />
                </div>
              </div>
            ))}
          </div>

          {/*
            Session AMP. The server PDF (scheduleOfFees.ts) has carried a Repairs note since Session
            AMH and this on-screen mirror did not, so the customer reading the modal saw the four
            call-out rows and nothing telling them a repair is priced differently. The wording points
            at the "each additional hour" COLUMN rather than naming a figure, exactly as the PDF does,
            which keeps it correct on all four rows at once: the out-of-hours and non-contract
            multipliers still apply to repair labour.
          */}
          {/* Session RC1 (Director's rules R1 to R4): the PDF's own Repairs wording, from the client mirror of fees.ts
              (lib/calloutCharge.ts repairsPublishedText, bound by repairPricing.test.ts), figures rendered by gbp2. */}
          <p>
            <strong>Repairs.</strong> {capitalise(repairsPublishedText(gbp2))}
          </p>
          {/*
            Director decision, 5 October 2026: labour is billed in 15-minute steps, rounded up, with a
            one-hour minimum. Mirrors the "Time on site" note on the server PDF (scheduleOfFees.ts) word
            for word, and reads the step from the client mirror of the pricing rule (lib/calloutCharge.ts,
            bound to fees.ts by labourRounding.test.ts) rather than typing 15 a second time.
          */}
          <p>
            <strong>Time on site.</strong>
            {` Labour is charged in ${LABOUR_INCREMENT_MINUTES}-minute steps, rounded up, with a minimum of one hour. For example, 1 hour 20 minutes on site is charged as 1 hour 30 minutes.`}
          </p>
          {/*
            Session AOC. The technician visit, added to this modal in the SAME session that added it
            to the server PDF (scheduleOfFees.ts section 2), rather than one following the other:
            the comment above records what happened last time the two drifted. AOC named every hourly
            basis in full; AOX replaced them with the Director's day-rate paragraph, still mirroring the
            PDF, because unlike the repair note there is no
            column on this page a reader could take the other figures from.
          */}
          {/*
            GUARDED, unlike the flat figures around it, and the difference is not fussiness. Every
            other line here reads a NUMBER off the payload, so a missing field renders badly and
            nothing more. This one reaches THROUGH an object, so on a payload without it the whole
            modal throws -- on the page a customer is signing a contract from. That payload is
            reachable in real life for a few minutes at a time: the SPA bundle and the API deploy as
            separate Render services, so a browser holding the new bundle can call a server still
            serving the old JSON. Rendering nothing there degrades the modal to exactly what it
            showed before this session, which is the correct fallback.
          */}
          {/*
            Session AOX. The paragraph is the Director's exact wording (16 September 2026), rendered
            from ONE string expression after the bold lead-in so the text content is word for word
            what he approved, with no JSX whitespace joins inside it; AcceptQuote.test.tsx asserts the
            whole paragraph as a single string. The figures are interpolated from the server payload
            rather than typed, so this modal and the PDF's section 2 row cannot disagree. Cover tier
            discounts are not mentioned here because the approved text says "whatever the cover tier
            held", which is the same rule stated positively.
          */}
          {techVisit && (
          <p>
            <strong>Technician visit.</strong>
            {` Where an engineer finds within the first hour of a call-out that the fault needs more than routine repair work, a specialist technician returns. We agree it with you before it's booked. A technician visit is ${gbp2Grouped(techVisit.dayRate)} per day, covering up to ${numberWord(techVisit.capHours)} hours on site, in or out of hours and in or out of contract. Time beyond ${numberWord(techVisit.capHours)} hours is charged at ${gbp2Grouped(techVisit.overageHourRate)} per hour in ${techVisit.overageStepMinutes}-minute steps, or as a further day at ${gbp2Grouped(techVisit.furtherDayRate)} covering a further ${numberWord(techVisit.capHours)} hours, with any time beyond that charged at the hourly rate. Travel is charged separately. A maintenance cover tier's discount does not reduce it.`}
          </p>
          )}
          {/*
            D296 (Director, 5 October 2026). Travel is billed one way, the journey to site, and the
            published Schedule of Fees PDF says so in its Travel note. This modal carried no travel note
            at all, so the same sentence is stated here, word for word as the PDF's lead.
          */}
          <p>
            <strong>Travel.</strong> Charged one way, for the journey to site only, per hour at the rate shown on your
            row above, calculated from whichever of our office or the engineer's current location is closer to site.
            The return journey is not charged.
          </p>
          <p>
            <strong>Full Condition Report</strong>, required before a service contract commences where there is no
            documented evidence of routine maintenance in the previous 12 months:{" "}
            {gbp2(rates.fullConditionReport)}.
          </p>
          <p className="cq-modal-footer">
            Non-contract works are paid by pro forma invoice, on receipt of invoice, or to the engineer on site.
          </p>
        </>
      )}

      <div className="cq-modal-buttons cq-sof-download">
        <a
          href={api.publicDocuments.scheduleOfFeesHref()}
          target="_blank"
          rel="noopener noreferrer"
          className="cq-info-btn"
        >
          Download PDF
        </a>
      </div>
    </Modal>
  );
}

// Share the quote's link (Session AAG). Uses the Web Share API where the browser supports it
// (phones and most modern browsers open the native share sheet); otherwise copies the current page
// URL to the clipboard with a brief "Link copied" confirmation replacing the label. The URL is
// always the current page: a persistent /quote/:token link, or whatever URL the page was opened
// from (a blob URL if saved and reopened offline). Exported for direct unit testing.
// documentLabel (Session AJV) names what kind of document this is in the native share title, e.g.
// "Maintenance Quote" or, on a renewal, "Service Agreement Renewal"; defaults to the original
// wording so every pre-existing caller is unaffected.
export function ShareButton({ quoteRef, documentLabel = "Maintenance Quote" }: { quoteRef: string; documentLabel?: string }) {
  const [copied, setCopied] = useState(false);
  async function onShare() {
    const url = window.location.href;
    const title = `${documentLabel} ${quoteRef} - East London Elevators`;
    const nav: Navigator | undefined = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav && typeof nav.share === "function") {
      try {
        await nav.share({ title, url });
      } catch {
        // The customer dismissed the native share sheet, or it failed; nothing to do.
      }
      return;
    }
    try {
      await nav?.clipboard?.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access blocked (rare, e.g. an insecure context); leave the label unchanged.
    }
  }
  return (
    <button
      type="button"
      onClick={onShare}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        background: "none",
        // The page's purple BORDER + TEXT tokens, both the full brand #7d21c7 since Session AHD
        // put the light canvas back (the hardcoded fallbacks here already were).
        border: "1px solid var(--cq-purple-border, #7d21c7)",
        borderRadius: "6px",
        padding: "6px 12px",
        font: "inherit",
        color: "var(--cq-purple-text)",
        cursor: "pointer",
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
      </svg>
      {copied ? "Link copied" : "Share Quote"}
    </button>
  );
}

// Exported so other customer-facing pages sharing this design system (the renewal accept page)
// render the SAME footer, including the skyline watermark, rather than a second implementation
// that can drift from this one's measured colours and placement.
export function Footer() {
  return (
    <footer className="ftr">
      <p>For and on behalf of East London Elevators.</p>
      <p className="co">East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
      <p className="co">
        {/* Session AHT: this page's own light canvas needs the darkened ink (flat #63C41F
            measures ~2:1 here); the component's shared default stays the flat green for its
            two dark-canvas callers (SubcontractorOnboarding, PortalShell). */}
        <WhatsAppLink color="#2f6d16" />
      </p>
      {/* Session ASQ (Stream G, Item 5): the privacy notice link, only once the notice is published. This footer sits on
          the page's LIGHT canvas (and prints), so it takes the page's own purple ink rather than the component's
          dark-canvas blue default (brand.md, shared components on more than one canvas). The renewal page reuses this
          Footer, so it carries the link too. */}
      <PrivacyNoticeLink className="co" color="var(--cq-purple-text)" />
      {/* Session ACE brand footer: purple rule, the London skyline strip and the dark
          footer bar (the email-chrome pattern). Screen only -- the printed copy keeps the
          plain footer above (dark blocks waste ink and the .cq-noprint rule drops them).
          The strip is the shared MASKED watermark (src/lib/skyline.ts), kept from Session
          AGZ: masking fills the artwork's alpha with an exact colour, where the raw line
          art is measurably invisible on any ground inside its own hue family (the AFJ
          finding -- no opacity value can fix that).
          Session AHD: the strip sits on the PAGE CANVAS, not on the dark footer bar below
          it, so when the canvas went back to light the near-white dark-surface fill would
          have been invisible -- the AFJ defect exactly mirrored. It takes the LIGHT pair
          from the same file, which skyline.ts measured for this case (#050816 at 0.35,
          chosen because it matches the dark pair's weight on both the peak and the body
          stroke). Judge any change to it on a composite rendered at the painted size. */}
      <div className="cq-skyline cq-noprint" aria-hidden="true">
        <div
          data-ele-skyline=""
          style={{
            width: "100%",
            height: SKYLINE_WATERMARK_HEIGHT,
            // Session AHR: page-scoped purple-toned ink (--cq-skyline-ink), not the shared
            // SKYLINE_WATERMARK_COLOUR_LIGHT -- see the variable's definition in AcceptQuote.css
            // for the measured reasoning (matches the PDF footer's purple treatment while
            // holding the same legibility the shared token had).
            backgroundColor: "var(--cq-skyline-ink)",
            opacity: SKYLINE_WATERMARK_OPACITY_LIGHT,
            WebkitMaskImage: "url('/brand/london-skyline-1200.png')",
            maskImage: "url('/brand/london-skyline-1200.png')",
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "bottom",
            maskPosition: "bottom",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        />
      </div>
      <div className="cq-footbar cq-noprint">
        <p>East London Elevators Ltd &middot; Company No. 15429089 &middot; VAT GB493339461</p>
        <p>Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE</p>
        <p>
          {/* Session AJW: the website was the only one of the three not clickable; a real anchor
              now matches the email link's existing pattern immediately before it. */}
          <b>{PHONE}</b> &middot; <a href="mailto:info@eastlondonelevators.co.uk">info@eastlondonelevators.co.uk</a> &middot;{" "}
          <a href="https://eastlondonelevators.co.uk">eastlondonelevators.co.uk</a>
        </p>
      </div>
    </footer>
  );
}
